import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowRight,
    faArrowTrendUp,
    faBolt,
    faCalendarCheck,
    faCheck,
    faCircleInfo,
    faClock,
    faCoins,
    faChevronDown,
    faChevronRight,
    faGift,
    faMedal,
    faRotate,
    faSearch,
    faShieldHalved,
    faStar,
    faStore,
    faTrophy,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
    applyAvoidableReworkPenalty,
    awardEmployeeOfMonth,
    cancelRedemption,
    createManualAdjustment,
    formatPoints,
    formatRewardDate,
    loadPendingQualityReviewContext,
    loadTicketRewardBreakdown,
    loadRewardHistoryPage,
    recordRewardIncident,
    requestRedemption,
    reviewRedemption,
    reviewTicketQuality,
    resolveRewardWorkUnit,
    searchRewardTickets,
    useRewardDashboard,
} from "./rewardClient";
import "./Rewards.css";

const REWARD_LABELS = {
    task_completed: "Task completed",
    speed_bonus: "Speed & quality",
    difficult_task: "Difficult task",
    urgent_management_request: "Urgent management request",
    perfect_week: "Perfect week",
    perfect_month: "Perfect month",
    speed_and_quality: "Speed & quality achievement",
    most_improved: "Most improved",
    avoidable_rework: "Incorrect completion — avoidable rework",
    manual_adjustment: "Manual adjustment",
    redemption: "Reward redeemed",
    overdue_daily_penalty: "Overdue task — daily deduction",
};

const TYPE_ICONS = {
    task_completed: faCheck,
    speed_bonus: faClock,
    difficult_task: faStar,
    urgent_management_request: faBolt,
    perfect_week: faCalendarCheck,
    perfect_month: faTrophy,
    speed_and_quality: faArrowTrendUp,
    most_improved: faBolt,
    employee_of_month: faMedal,
    avoidable_rework: faXmark,
    manual_adjustment: faShieldHalved,
    redemption: faGift,
    overdue_daily_penalty: faClock,
};

const QUALITY_REWARD_POINTS = {
    difficult_task: 5,
    urgent_management_request: 10,
};

const ACHIEVEMENT_DEFINITIONS = [
    { type: "perfect_week", title: "Perfect week", points: 25, icon: faCalendarCheck, description: "An active working week without any overdue task." },
    { type: "perfect_month", title: "Perfect month", points: 100, icon: faTrophy, description: "No overdue work, quality corrections, avoidable rework, or recorded complaints." },
    { type: "speed_and_quality", title: "Speed & quality", points: "50–100", icon: faBolt, description: "A strong volume of fast completions with no later quality failures." },
    { type: "most_improved", title: "Most improved", points: 100, icon: faArrowTrendUp, description: "The largest qualifying month-on-month improvement when enabled by policy." },
    { type: "employee_of_month", title: "Employee of the month", points: "£50–£100", icon: faMedal, description: "Management recognition for performance, support, and reliability." },
    // { type: "team_target", title: "Team target", points: "Team reward", icon: faBolt, description: "A shared target linked to a lunch, activity, or early finish." },
];

const REWARD_ROLE_LABELS = {
    admin: "Admin",
    inspection: "Inspection",
    coordinator: "Coordinator",
    reviewer: "Reviewer",
    remedial: "Remedial",
    manager: "Manager",
};

const teamUserLabel = (employee) => {
    const name = employee?.name || employee?.email || employee?.id || "Unknown user";
    const role = employee?.user_role === "coordinator2" ? "coordinator" : employee?.user_role;
    return `${name} — ${REWARD_ROLE_LABELS[role] || role || "User"}`;
};

const POINT_RULES = [
    { points: "+1", title: "Task completed", description: "Automatically awarded when an eligible ticket work unit is completed." },
    { points: "+1", title: "Within 10 minutes", description: "Automatically confirmed when recorded active work is 10 minutes or less. If the window is exceeded, this bonus is unavailable and excluded from potential points." },
    { points: "+5", title: "Difficult task", description: "Classified difficult work is reviewed by management after completion." },
    { points: "+10", title: "Urgent request", description: "A genuine urgent management request is reviewed by management after completion." },
    { points: "+25", title: "Perfect week", description: "Eligible activity with no task becoming overdue." },
    { points: "−1/day", title: "Overdue task", description: "1 point is deducted for each overdue task on every overdue calendar day. Each task/day can only be charged once.", penalty: true },
    { points: "−20", title: "Avoidable rework", description: "Exceptional admin-only penalty with a recorded reason.", penalty: true },
];

const emptyDashboard = {
    summary: { confirmed_points: 0, pending_points: 0, reserved_points: 0, available_points: 0 },
    current_period: { confirmed_points: 0, pending_points: 0 },
    history: [],
    achievements: [],
    catalog: [],
    redemptions: [],
    pending_redemptions: [],
    pending_quality: [],
    recent_incidents: [],
    recent_awards: [],
    recent_audit: [],
    team_users: [],
    viewer: { role: "" },
    user: {},
};

const Points = ({ value, sign = false }) => {
    const number = Number(value || 0);
    return <>{sign && number > 0 ? "+" : ""}{number.toLocaleString("en-GB")}</>;
};

const achievementRewardLabel = (achievement) => {
    if (achievement?.type === "employee_of_month") {
        const cashValue = Number(achievement.cash_value_gbp || 0);
        return cashValue > 0 ? `£${cashValue.toLocaleString("en-GB")}` : "Earned";
    }
    const points = Number(achievement?.points || 0);
    return points > 0 ? `+${points.toLocaleString("en-GB")} pts` : "Earned";
};

const achievementDefinitionRewardLabel = (definition) => {
    return typeof definition.points === "number" ? `+${definition.points.toLocaleString("en-GB")} pts` : definition.points;
};

const StatusPill = ({ status }) => {
    const labels = {
        pending: "Pending",
        confirmed: "Confirmed",
        revoked: "Revoked",
        approved: "Approved",
        rejected: "Rejected",
        cancelled: "Cancelled",
        partially_confirmed: "Mixed",
        reviewed: "Reviewed",
    };
    return <span className={`rw-status is-${status}`}>{labels[status] || status}</span>;
};

const EmptyState = ({ icon = faCircleInfo, title, children }) => {
    return (
        <div className="rw-empty">
            <span><FontAwesomeIcon icon={icon} /></span>
            <div><strong>{title}</strong><p>{children}</p></div>
        </div>
    );
};

const rewardCategory = (item) => {
    const text = `${item?.category || ""} ${item?.title || ""}`.toLowerCase();
    if (/time|leave|holiday|early|half.?day/.test(text)) return "time";
    if (/voucher|bonus|cash|gift card/.test(text)) return "voucher";
    if (/experience|spa|concert|driving|activity|restaurant/.test(text)) return "experience";
    if (/food|drink|coffee|tea|breakfast|lunch|meal/.test(text)) return "everyday";
    return "other";
};

const CATEGORY_LABELS = {
    all: "All",
    everyday: "Food & drink",
    voucher: "Vouchers",
    time: "Time off",
    experience: "Experiences",
    other: "Other",
};

const RewardIcon = ({ item }) => {
    const category = rewardCategory(item);
    const icon = category === "time" ? faClock : category === "experience" ? faStar : category === "voucher" ? faGift : faCoins;
    if (item.image_url) return <span className={`rw-reward-icon is-${category}`}><img src={item.image_url} alt="" /></span>;
    return <span className={`rw-reward-icon is-${category}`}><FontAwesomeIcon icon={icon} /></span>;
};

const PortalLayer = ({ children, onClose, label, wide = false, drawer = false, variant = "" }) => {
    const layerRef = useRef(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        const previouslyFocused = document.activeElement;
        const rewardsScroller = document.querySelector(".rw-rewards-window .rw-page.is-modal > .rw-page__body");
        const rewardsScrollTop = rewardsScroller?.scrollTop ?? 0;
        layerRef.current?.focus({ preventScroll: true });
        const onKeyDown = (event) => {
            if (event.key === "Escape") onCloseRef.current?.();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            if (rewardsScroller && document.contains(rewardsScroller)) {
                rewardsScroller.scrollTop = rewardsScrollTop;
            }
            if (previouslyFocused instanceof HTMLElement && document.contains(previouslyFocused)) {
                previouslyFocused.focus({ preventScroll: true });
            }
        };
    }, []);

    if (typeof document === "undefined") return null;

    return createPortal(
        <div
            className={`rw-layer-overlay${drawer ? " is-drawer" : ""}`}
            data-rw-layer="true"
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose?.();
            }}
        >
            <section
                className={`${drawer ? "rw-layer-drawer" : "rw-layer-dialog"}${wide ? " is-wide" : ""}${variant ? ` is-${variant}` : ""}`}
                ref={layerRef}
                role="dialog"
                aria-modal="true"
                aria-label={label}
                tabIndex={-1}
                onMouseDown={(event) => event.stopPropagation()}
            >
                {children}
            </section>
        </div>,
        document.body
    );
};

const ModalSurface = ({ children, onClose, label, wide = false }) => (
    <PortalLayer onClose={onClose} label={label} wide={wide}>
        <div className={`rw-dialog${wide ? " is-wide" : ""}`}>{children}</div>
    </PortalLayer>
);

const DrawerSurface = ({ children, onClose, label, variant = "" }) => (
    <PortalLayer onClose={onClose} label={label} drawer variant={variant}>
        <aside className="rw-review-drawer">{children}</aside>
    </PortalLayer>
);

const RewardRequestDialog = ({ item, available, busy, onClose, onConfirm }) => {
    const cost = Number(item.cost_points || 0);
    const after = Math.max(0, available - cost);
    return (
        <ModalSurface onClose={onClose} label="Request reward">
            <div className="rw-dialog__header">
                <div className="rw-dialog__title-group"><span className="rw-dialog__mark"><FontAwesomeIcon icon={faGift} /></span><div><span className="rw-eyebrow">Redemption</span><h2>Redeem {item.title}?</h2><p>Your request goes to management for approval.</p></div></div>
                <button type="button" className="rw-icon-button" onClick={onClose} disabled={busy} aria-label="Close"><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <div className="rw-redemption-preview">
                <RewardIcon item={item} />
                <div><strong>{item.title}</strong><span>{cost.toLocaleString("en-GB")} points</span></div>
            </div>
            <div className="rw-balance-change"><span>Your balance</span><strong>{available.toLocaleString("en-GB")} pts <b>→</b> {after.toLocaleString("en-GB")} pts</strong></div>
            <p className="rw-dialog-note">Points are reserved immediately and only deducted permanently when the request is approved.</p>
            <div className="rw-dialog__actions">
                <button className="rw-button is-secondary" type="button" onClick={onClose} disabled={busy}>Cancel</button>
                <button className="rw-button is-primary" type="button" onClick={onConfirm} disabled={busy}>{busy ? "Requesting…" : "Request reward"}</button>
            </div>
        </ModalSurface>
    );
};

const RulesDialog = ({ onClose }) => {
    return (
        <ModalSurface onClose={onClose} label="How points work">
            <div className="rw-dialog__header">
                <div className="rw-dialog__title-group"><span className="rw-dialog__mark"><FontAwesomeIcon icon={faShieldHalved} /></span><div><span className="rw-eyebrow">Reward rules</span><h2>How points work</h2><p>Completion and Speed &amp; quality are automatic. Difficult and Urgent rewards require management review.</p></div></div>
                <button type="button" className="rw-icon-button" onClick={onClose} aria-label="Close"><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <div className="rw-rule-detail">
                {POINT_RULES.map((rule) => (
                    <div className={`rw-rule-detail__row${rule.penalty ? " is-penalty" : ""}`} key={`${rule.points}-${rule.title}`}>
                        <strong>{rule.points}</strong><div><b>{rule.title}</b><span>{rule.description}</span></div>
                    </div>
                ))}
            </div>
            <div className="rw-quality-note"><strong>Review.</strong> Difficult (+5) and Urgent management request (+10) remain pending until an admin approves or rejects each reward separately.</div>
            <div className="rw-dialog__actions"><button className="rw-button is-primary" type="button" onClick={onClose}>Got it</button></div>
        </ModalSurface>
    );
};

const rewardState = (item, dashboard) => {
    const available = Number(dashboard.summary?.available_points ?? dashboard.summary?.confirmed_points ?? 0);
    const pending = (dashboard.redemptions || []).find((request) => request.status === "pending" && request.item_id === item.id);
    const cost = Number(item.cost_points || 0);
    const soldOut = Number.isInteger(item.inventory_count) && item.inventory_count <= 0;
    return {
        available,
        pending,
        cost,
        soldOut,
        affordable: available >= cost,
        toUnlock: Math.max(0, cost - available),
    };
};

const RewardCard = ({ item, dashboard, onRequest, onCancel, busyId, featured = false, readOnly = false, highlighted = false, cardRef = null }) => {
    const state = rewardState(item, dashboard);
    const progress = state.cost > 0 ? Math.max(0, Math.min(100, (state.available / state.cost) * 100)) : 100;
    return (
        <article ref={cardRef} className={`rw-reward-card${featured ? " is-featured" : ""}${state.pending ? " is-pending" : state.soldOut ? " is-sold-out" : state.affordable ? " is-affordable" : " is-locked"}${highlighted ? " is-notification-highlight" : ""}`}>
            <div className="rw-reward-card__head">
                <div className="rw-reward-card__identity"><RewardIcon item={item} /><span className="rw-reward-category">{CATEGORY_LABELS[rewardCategory(item)] || "Reward"}</span></div>
                {state.pending && <span className="rw-card-status is-pending">Approval pending</span>}
            </div>
            <div className="rw-reward-card__copy">
                <h3>{item.title}</h3>
                <p>{item.description || "Redeem this reward with confirmed points."}</p>
            </div>
            {!state.affordable && !state.pending && !state.soldOut && (
                <div className="rw-reward-card__progress" aria-label={`${state.available} of ${state.cost} points`}><span style={{ width: `${progress}%` }} /></div>
            )}
            <div className="rw-reward-card__foot">
                <strong>{state.cost.toLocaleString("en-GB")} <span>pts</span></strong>
                {state.pending ? (
                    readOnly ? <span className="rw-muted-action">Pending request</span> : <button type="button" className="rw-link-danger" disabled={busyId === state.pending.id} onClick={() => onCancel(state.pending)}>{busyId === state.pending.id ? "Cancelling…" : "Cancel request"}</button>
                ) : state.soldOut ? (
                    <span className="rw-muted-action">Unavailable</span>
                ) : state.affordable ? (
                    readOnly ? <span className="rw-view-only-action">View only</span> : <button type="button" className="rw-card-action" onClick={() => onRequest(item)}>Redeem <FontAwesomeIcon icon={faArrowRight} /></button>
                ) : (
                    <span className="rw-unlock-copy">{state.toUnlock.toLocaleString("en-GB")} pts away</span>
                )}
            </div>
        </article>
    );
};

const getNextReward = (dashboard) => {
    const available = Number(dashboard.summary?.available_points ?? dashboard.summary?.confirmed_points ?? 0);
    const sorted = [...(dashboard.catalog || [])].sort((a, b) => Number(a.cost_points || 0) - Number(b.cost_points || 0));
    return sorted.find((item) => Number(item.cost_points || 0) > available) || null;
};

const selectHomeRewards = (dashboard) => {
    const available = Number(dashboard.summary?.available_points ?? dashboard.summary?.confirmed_points ?? 0);
    const catalog = [...(dashboard.catalog || [])].filter((item) => !(Number.isInteger(item.inventory_count) && item.inventory_count <= 0));
    const sorted = catalog.sort((a, b) => Number(a.cost_points || 0) - Number(b.cost_points || 0));
    const affordable = sorted.filter((item) => Number(item.cost_points || 0) <= available);
    const next = sorted.find((item) => Number(item.cost_points || 0) > available);
    const picks = [];
    const add = (item) => { if (item && !picks.some((pick) => pick.id === item.id)) picks.push(item); };
    add(affordable[0]);
    add(affordable[affordable.length - 1]);
    add(next);
    sorted.forEach((item) => { if (picks.length < 3) add(item); });
    return picks.slice(0, 3);
};

const RewardsHero = ({ dashboard, onRules }) => {
    const summary = dashboard.summary || {};
    const available = Number(summary.available_points ?? summary.confirmed_points ?? 0);
    const pending = Number(summary.pending_points || 0);
    const periodConfirmed = Number(dashboard.current_period?.confirmed_points || 0);
    const periodTotal = periodConfirmed;
    const nextReward = getNextReward(dashboard);
    const nextCost = Number(nextReward?.cost_points || 0);
    const remaining = nextReward ? Math.max(0, nextCost - available) : 0;
    const progress = nextReward && nextCost > 0 ? Math.max(0, Math.min(100, (available / nextCost) * 100)) : 100;

    return (
        <section className="rw-home-hero" aria-label="Rewards summary">
            <article className="rw-summary-card is-balance">
                <span className="rw-summary-card__icon"><FontAwesomeIcon icon={faCoins} /></span>
                <div className="rw-summary-card__copy">
                    <span>Available points</span>
                    <strong>{available.toLocaleString("en-GB")} <small>pts</small></strong>
                    <p>Confirmed and ready to redeem</p>
                </div>
            </article>

            <article className="rw-summary-card">
                <span className="rw-summary-card__icon"><FontAwesomeIcon icon={faArrowTrendUp} /></span>
                <div className="rw-summary-card__copy">
                    <span>Earned this period</span>
                    <strong className={periodTotal > 0 ? "is-positive" : ""}>{periodTotal > 0 ? "+" : ""}{periodTotal.toLocaleString("en-GB")} <small>pts</small></strong>
                    <p>Confirmed this period</p>
                </div>
            </article>

            <article className="rw-summary-card">
                <span className="rw-summary-card__icon is-pending"><FontAwesomeIcon icon={faClock} /></span>
                <div className="rw-summary-card__copy">
                    <span>Pending review</span>
                    <strong>{pending.toLocaleString("en-GB")} <small>pts</small></strong>
                    <p>Difficult or urgent rewards awaiting review</p>
                </div>
            </article>

            <article className="rw-summary-card is-next">
                <span className="rw-summary-card__icon"><FontAwesomeIcon icon={faGift} /></span>
                <div className="rw-summary-card__copy rw-summary-next">
                    {nextReward ? (
                        <>
                            <div className="rw-summary-next__head">
                                <div><span>Next reward</span><strong>{nextReward.title}</strong></div>
                                <b>{remaining.toLocaleString("en-GB")} pts away</b>
                            </div>
                            <div className="rw-milestone-track"><span style={{ width: `${progress}%` }} /></div>
                        </>
                    ) : (
                        <div className="rw-summary-next__head">
                            <div><span>Next reward</span><strong>{dashboard.catalog.length ? "All current rewards are within reach" : "No rewards available yet"}</strong></div>
                        </div>
                    )}
                    <button type="button" className="rw-text-button" onClick={onRules}>How points work <FontAwesomeIcon icon={faArrowRight} /></button>
                </div>
            </article>
        </section>
    );
};

const HomeRewardRow = ({ item, dashboard, onRequest, onCancel, busyId, readOnly = false }) => {
    const state = rewardState(item, dashboard);
    return (
        <article className={`rw-home-reward-row${state.pending ? " is-pending" : state.soldOut ? " is-sold-out" : state.affordable ? " is-affordable" : " is-locked"}`}>
            <RewardIcon item={item} />
            <div className="rw-home-reward-row__copy">
                <div><h3>{item.title}</h3><span>{CATEGORY_LABELS[rewardCategory(item)] || "Reward"}</span></div>
                <p>{item.description || "Redeem this reward with confirmed points."}</p>
            </div>
            <div className="rw-home-reward-row__value">
                <strong>{state.cost.toLocaleString("en-GB")} <span>pts</span></strong>
                {state.pending ? (
                    readOnly ? <span className="rw-muted-action">Pending</span> : <button type="button" className="rw-link-danger" disabled={busyId === state.pending.id} onClick={() => onCancel(state.pending)}>{busyId === state.pending.id ? "Cancelling…" : "Cancel"}</button>
                ) : state.soldOut ? (
                    <span className="rw-muted-action">Unavailable</span>
                ) : state.affordable ? (
                    readOnly ? <span className="rw-view-only-action">View only</span> : <button type="button" className="rw-card-action" onClick={() => onRequest(item)}>Redeem</button>
                ) : (
                    <span className="rw-unlock-copy">{state.toUnlock.toLocaleString("en-GB")} away</span>
                )}
            </div>
        </article>
    );
};

const RecognitionStrip = ({ dashboard, onViewAll }) => {
    const rows = (dashboard.achievements || []).slice(0, 3).map((achievement) => ({
        id: achievement.id,
        title: achievement.title || REWARD_LABELS[achievement.type] || achievement.type,
        value: achievementRewardLabel(achievement),
        icon: TYPE_ICONS[achievement.type] || faTrophy,
    }));

    return (
        <section className="rw-recognition-strip">
            <div className="rw-recognition-strip__title"><div><h2>Recognition</h2><p>Recent achievements and awards.</p></div><button type="button" className="rw-text-button" onClick={onViewAll}>View all <FontAwesomeIcon icon={faArrowRight} /></button></div>
            {rows.length ? (
                <div className="rw-recognition-items">
                    {rows.map((row) => <div key={row.id}><span><FontAwesomeIcon icon={row.icon} /></span><strong>{row.title}</strong><b>{row.value}</b></div>)}
                </div>
            ) : (
                <div className="rw-recognition-empty"><span><FontAwesomeIcon icon={faTrophy} /></span><div><strong>No recognition yet</strong><small>Achievements you earn will appear here.</small></div></div>
            )}
        </section>
    );
};

const OverviewActivity = ({ dashboard, onViewHistory }) => {
    const history = dashboard.history || [];
    const confirmedEntries = history.filter((entry) => (entry.status || "confirmed") === "confirmed");
    const earnedFallback = confirmedEntries.filter((entry) => Number(entry.points || 0) > 0).reduce((sum, entry) => sum + Number(entry.points || 0), 0);
    const earned = Number(dashboard.summary?.lifetime_positive_points ?? earnedFallback);
    const redemptionEntries = confirmedEntries.filter((entry) => entry.type === "redemption" && Number(entry.points || 0) < 0);
    const redeemed = Math.abs(redemptionEntries.reduce((sum, entry) => sum + Number(entry.points || 0), 0));
    const deductionEntries = confirmedEntries.filter((entry) => Number(entry.points || 0) < 0 && entry.type !== "redemption");
    const overdueEntries = deductionEntries.filter((entry) => entry.type === "overdue_daily_penalty");
    const otherDeductions = deductionEntries.filter((entry) => entry.type !== "overdue_daily_penalty");
    const lost = Math.abs(otherDeductions.reduce((sum, entry) => sum + Number(entry.points || 0), 0));
    const overdueLost = Math.abs(overdueEntries.reduce((sum, entry) => sum + Number(entry.points || 0), 0));
    const recent = history.slice(0, 4);

    return (
        <section className="rw-overview-detail" aria-label="Points overview and recent activity">
            <div className="rw-overview-stat-grid" aria-label="Points breakdown">
                <div className="rw-overview-stat"><span className="rw-overview-stat__icon"><FontAwesomeIcon icon={faCoins} /></span><div><span>Lifetime earned</span><strong className="is-positive">+{earned.toLocaleString("en-GB")}</strong><small>Confirmed points earned</small></div></div>
                <div className="rw-overview-stat"><span className="rw-overview-stat__icon"><FontAwesomeIcon icon={faGift} /></span><div><span>Points redeemed</span><strong>{redeemed.toLocaleString("en-GB")}</strong><small>Used on approved rewards</small></div></div>
                <div className="rw-overview-stat"><span className="rw-overview-stat__icon"><FontAwesomeIcon icon={faShieldHalved} /></span><div><span>Corrections</span><strong className={lost > 0 ? "is-negative" : ""}>{lost > 0 ? `−${lost.toLocaleString("en-GB")}` : "0"}</strong><small>Non-overdue deductions</small></div></div>
                <div className="rw-overview-stat"><span className="rw-overview-stat__icon"><FontAwesomeIcon icon={faClock} /></span><div><span>Overdue penalties</span><strong className={overdueLost > 0 ? "is-negative" : ""}>{overdueLost > 0 ? `−${overdueLost.toLocaleString("en-GB")}` : "0"}</strong><small>{overdueEntries.length} daily {overdueEntries.length === 1 ? "charge" : "charges"}</small></div></div>
            </div>

            <div className="rw-overview-recent">
                <div className="rw-overview-recent__head"><div><h2>Recent points activity</h2><p>Latest changes to your points.</p></div><button type="button" className="rw-text-button" onClick={onViewHistory}>View history <FontAwesomeIcon icon={faArrowRight} /></button></div>
                {recent.length ? <div className="rw-overview-recent__list">
                    {recent.map((entry) => {
                        const status = entry.status || "confirmed";
                        const points = Number(entry.points || 0);
                        const statusCopy = status === "pending" ? "Awaiting review" : status === "rejected" ? "Declined" : status === "revoked" ? "Removed" : "";
                        return <div className={`rw-overview-activity-row is-${status}`} key={entry.id}>
                            <span className="rw-overview-activity-row__icon"><FontAwesomeIcon icon={TYPE_ICONS[entry.type] || faCoins} /></span>
                            <div className="rw-overview-activity-row__copy"><div><strong>{entry.title || REWARD_LABELS[entry.type] || "Reward activity"}</strong>{status !== "confirmed" && <StatusPill status={status} />}</div><small>{entry.ticket_title || "Account activity"} · {formatRewardDate(entry.created_at || entry.earned_at)}{statusCopy ? ` · ${statusCopy}` : ""}</small></div>
                            <b className={status === "pending" ? "is-pending-points" : points >= 0 ? "is-positive" : "is-negative"}>{formatPoints(points, { sign: true })} pts</b>
                        </div>;
                    })}
                </div> : <EmptyState icon={faCoins} title="No points activity yet">Completed work, rewards, and deductions will appear here.</EmptyState>}
            </div>
        </section>
    );
};

const RewardsHome = ({ dashboard, refresh, notify, setView, readOnly = false }) => {
    const [selectedReward, setSelectedReward] = useState(null);
    const [rulesOpen, setRulesOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [cancelBusy, setCancelBusy] = useState("");
    const available = Number(dashboard.summary?.available_points ?? dashboard.summary?.confirmed_points ?? 0);
    const picks = selectHomeRewards(dashboard);

    const submitRequest = async () => {
        if (!selectedReward || busy || readOnly) return;
        setBusy(true);
        try {
            await requestRedemption(selectedReward.id);
            notify("success", `${selectedReward.title} has been requested.`);
            setSelectedReward(null);
            await refresh();
        } catch (error) {
            notify("error", error.message);
        } finally {
            setBusy(false);
        }
    };

    const cancel = async (request) => {
        if (readOnly) return;
        setCancelBusy(request.id);
        try {
            await cancelRedemption(request.id);
            notify("success", "Reward request cancelled and reserved points released.");
            await refresh();
        } catch (error) {
            notify("error", error.message);
        } finally {
            setCancelBusy("");
        }
    };

    return (
        <div className="rw-home">
            <RewardsHero dashboard={dashboard} onRules={() => setRulesOpen(true)} />
            <OverviewActivity dashboard={dashboard} onViewHistory={() => setView("history")} />

            <div className="rw-home-body">
                <section className="rw-home-rewards" aria-labelledby="rw-home-rewards-title">
                    <div className="rw-section-heading">
                        <div className="rw-section-heading__copy">
                            <h2 id="rw-home-rewards-title">Rewards</h2>
                            <p>Redeem confirmed points or see what is within reach.</p>
                        </div>
                        <button type="button" className="rw-text-button rw-section-heading__action" onClick={() => setView("catalog")}>View all <FontAwesomeIcon icon={faArrowRight} /></button>
                    </div>
                    {picks.length ? (
                        <div className="rw-home-reward-list">
                            {picks.map((item) => <HomeRewardRow key={item.id} item={item} dashboard={dashboard} onRequest={setSelectedReward} onCancel={cancel} busyId={cancelBusy} readOnly={readOnly} />)}
                        </div>
                    ) : <EmptyState icon={faStore} title="No rewards available">Your confirmed points will stay available until rewards are added.</EmptyState>}
                </section>

                <RecognitionStrip dashboard={dashboard} onViewAll={() => setView("achievements")} />
            </div>

            {selectedReward && <RewardRequestDialog item={selectedReward} available={available} busy={busy} onClose={() => !busy && setSelectedReward(null)} onConfirm={submitRequest} />}
            {rulesOpen && <RulesDialog onClose={() => setRulesOpen(false)} />}
        </div>
    );
};

const CatalogView = ({ dashboard, refresh, notify, readOnly = false, highlightTarget = null }) => {
    const [filter, setFilter] = useState("all");
    const [selectedReward, setSelectedReward] = useState(null);
    const [requestBusy, setRequestBusy] = useState(false);
    const [cancelBusy, setCancelBusy] = useState("");
    const highlightCardRef = useRef(null);
    const available = Number(dashboard.summary?.available_points ?? dashboard.summary?.confirmed_points ?? 0);
    const highlightedRedemption = (dashboard.redemptions || []).find((request) => request.id === highlightTarget?.metadata?.redemption_id);
    const highlightedItemId = highlightedRedemption?.item_id || (dashboard.catalog || []).find((item) => item.title === highlightTarget?.metadata?.item_title)?.id || null;
    const categories = ["everyday", "voucher", "time", "experience", "other"].filter((key) => dashboard.catalog.some((item) => rewardCategory(item) === key));
    const filtered = dashboard.catalog.filter((item) => filter === "all" || rewardCategory(item) === filter);

    const submitRequest = async () => {
        if (!selectedReward || requestBusy || readOnly) return;
        setRequestBusy(true);
        try {
            await requestRedemption(selectedReward.id);
            notify("success", `${selectedReward.title} has been requested.`);
            setSelectedReward(null);
            await refresh();
        } catch (error) {
            notify("error", error.message);
        } finally {
            setRequestBusy(false);
        }
    };

    const cancel = async (request) => {
        if (readOnly) return;
        setCancelBusy(request.id);
        try {
            await cancelRedemption(request.id);
            notify("success", "Reward request cancelled.");
            await refresh();
        } catch (error) {
            notify("error", error.message);
        } finally {
            setCancelBusy("");
        }
    };

    useEffect(() => {
        if (!highlightTarget?.id) return;
        setFilter("all");
    }, [highlightTarget?.id]);

    useEffect(() => {
        if (!highlightedItemId || !highlightCardRef.current) return undefined;
        const timer = window.setTimeout(() => highlightCardRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" }), 80);
        return () => window.clearTimeout(timer);
    }, [highlightTarget?.id, highlightedItemId]);

    return (
        <div className="rw-focus-view">
            <FocusHeader title="Rewards shop" subtitle="Choose a reward using confirmed points." actions={<div className="rw-balance-chip"><FontAwesomeIcon icon={faCoins} /><strong>{available.toLocaleString("en-GB")}</strong><span>pts available</span></div>} />
            <div className="rw-category-filters" role="group" aria-label="Filter rewards">
                {["all", ...categories].map((key) => <button key={key} type="button" className={filter === key ? "is-active" : ""} onClick={() => setFilter(key)}>{CATEGORY_LABELS[key]}</button>)}
            </div>
            {filtered.length ? <div className="rw-catalog-grid">{filtered.map((item) => <RewardCard key={item.id} item={item} dashboard={dashboard} onRequest={setSelectedReward} onCancel={cancel} busyId={cancelBusy} readOnly={readOnly} highlighted={item.id === highlightedItemId} cardRef={item.id === highlightedItemId ? highlightCardRef : null} />)}</div> : <EmptyState icon={faStore} title="No rewards in this category">Choose another category to see the available catalog.</EmptyState>}
            {selectedReward && <RewardRequestDialog item={selectedReward} available={available} busy={requestBusy} onClose={() => !requestBusy && setSelectedReward(null)} onConfirm={submitRequest} />}
        </div>
    );
};

const FocusHeader = ({ eyebrow = "", title, subtitle, onBack = null, actions = null }) => {
    return (
        <header className="rw-focus-header">
            <div className="rw-focus-header__left">
                {onBack && <button type="button" className="rw-back-button" onClick={onBack}>←</button>}
                <div className="rw-heading-block">{eyebrow && <span className="rw-eyebrow">{eyebrow}</span>}<h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
            </div>
            {actions && <div className="rw-focus-header__actions">{actions}</div>}
        </header>
    );
};

const rewardEntryTime = (entry) => {
    const value = entry?.created_at || entry?.earned_at || entry?.updated_at || null;
    if (typeof value?.toMillis === "function") return value.toMillis();
    if (typeof value?.seconds === "number") return value.seconds * 1000;
    const parsed = value ? new Date(value).getTime() : 0;
    return Number.isFinite(parsed) ? parsed : 0;
};

const rewardNotificationView = (target) => {
    const type = String(target?.type || "");
    const metadata = target?.metadata || {};
    if (type === "reward_achievement") return "achievements";
    if (type === "reward_redemption_requested" || type === "reward_redemption_cancelled") return "catalog";
    if (type === "reward_redemption_reviewed" && metadata.decision !== "approved") return "catalog";
    return "history";
};

const rewardNotificationLedgerIds = (target) => {
    const metadata = target?.metadata || {};
    const ids = new Set();
    if (metadata.ledger_id) ids.add(String(metadata.ledger_id));
    (Array.isArray(metadata.reward_rows) ? metadata.reward_rows : []).forEach((row) => {
        if (row?.ledger_id) ids.add(String(row.ledger_id));
    });
    return ids;
};

const rewardHistoryEntryMatchesTarget = (entry, target) => {
    if (!entry?.id || !target) return false;
    const metadata = target.metadata || {};
    const entryMetadata = entry.metadata || {};
    const ledgerIds = rewardNotificationLedgerIds(target);
    if (ledgerIds.has(String(entry.id))) return true;
    if (metadata.redemption_id && entryMetadata.redemption_id === metadata.redemption_id) return true;
    if (metadata.source === "avoidable_rework") {
        const sameTicket = !target.ticket_id || entry.ticket_id === target.ticket_id;
        const sameWorkUnit = !metadata.work_unit_id || entry.work_unit_id === metadata.work_unit_id;
        return sameTicket && sameWorkUnit && (entry.type === "avoidable_rework" || String(entry.type || "").endsWith("_reversal"));
    }
    if (metadata.source === "overdue_daily_penalty") {
        return entry.type === "overdue_daily_penalty"
            && (!target.ticket_id || entry.ticket_id === target.ticket_id)
            && (!metadata.work_unit_id || entry.work_unit_id === metadata.work_unit_id)
            && (!metadata.overdue_date || entryMetadata.overdue_date === metadata.overdue_date);
    }
    return false;
};

const rewardHistoryGroupMatchesTarget = (group, target) => {
    if (!group || !target) return false;
    if (group.entries.some((entry) => rewardHistoryEntryMatchesTarget(entry, target))) return true;
    return Boolean(target.ticket_id && group.ticketId === target.ticket_id);
};

const rewardAchievementType = (target) => {
    const metadata = target?.metadata || {};
    if (metadata.achievement_type) return metadata.achievement_type;
    if (metadata.source === "employee_of_month") return "employee_of_month";
    return null;
};

const mergeRewardHistoryEntries = (primary = [], secondary = []) => {
    const byId = new Map();
    [...primary, ...secondary].forEach((entry) => {
        if (entry?.id) byId.set(entry.id, entry);
    });
    return [...byId.values()].sort((a, b) => rewardEntryTime(b) - rewardEntryTime(a));
};

const usePagedRewardHistory = (dashboard) => {
    const userId = dashboard.user?.id || "";
    const sourceHistory = dashboard.history || [];
    const [entries, setEntries] = useState(sourceHistory);
    const [cursor, setCursor] = useState(dashboard.history_cursor || null);
    const [hasMore, setHasMore] = useState(Boolean(dashboard.history_has_more));
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadedOlder, setLoadedOlder] = useState(false);
    const [loadedUserId, setLoadedUserId] = useState(userId);

    useEffect(() => {
        if (userId !== loadedUserId) {
            setLoadedUserId(userId);
            setEntries(sourceHistory);
            setCursor(dashboard.history_cursor || null);
            setHasMore(Boolean(dashboard.history_has_more));
            setLoadedOlder(false);
            return;
        }
        if (!userId) return;
        setEntries((current) => mergeRewardHistoryEntries(sourceHistory, current));
        if (!loadedOlder) {
            setCursor(dashboard.history_cursor || null);
            setHasMore(Boolean(dashboard.history_has_more));
        }
    }, [userId, loadedUserId, sourceHistory, dashboard.history_cursor, dashboard.history_has_more, loadedOlder]);

    const loadMore = async () => {
        if (!userId || !hasMore || loadingMore) return;
        setLoadingMore(true);
        try {
            const page = await loadRewardHistoryPage(userId, cursor);
            setEntries((current) => mergeRewardHistoryEntries(current, page.items));
            setCursor(page.cursor);
            setHasMore(page.hasMore);
            setLoadedOlder(true);
        } finally {
            setLoadingMore(false);
        }
    };

    return { entries, hasMore, loadingMore, loadMore };
};

const groupRewardHistory = (entries = []) => {
    const groups = new Map();
    entries.forEach((entry) => {
        const ticketId = entry.ticket_id || entry.task_id || null;
        const key = ticketId ? `ticket:${ticketId}` : `entry:${entry.id}`;
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                ticketId,
                title: entry.ticket_title || (ticketId ? "Untitled task" : "Rewards activity"),
                entries: [],
                latest: 0,
            });
        }
        const group = groups.get(key);
        if ((!group.title || group.title === "Untitled task") && entry.ticket_title) group.title = entry.ticket_title;
        group.entries.push(entry);
        group.latest = Math.max(group.latest, rewardEntryTime(entry));
    });
    return [...groups.values()]
        .map((group) => ({ ...group, entries: [...group.entries].sort((a, b) => rewardEntryTime(b) - rewardEntryTime(a)) }))
        .sort((a, b) => b.latest - a.latest);
};

const TicketHistoryGroups = ({ entries, onOpenTicket, emptyTitle = "No reward history yet", emptyText = "Reward activity will appear here.", pageSize = 5, resetKey = "", highlightTarget = null }) => {
    const groups = useMemo(() => groupRewardHistory(entries), [entries]);
    const [page, setPage] = useState(0);
    const [expandedKeys, setExpandedKeys] = useState(() => new Set());
    const groupRefs = useRef({});
    const entryRefs = useRef({});
    const pageCount = Math.max(1, Math.ceil(groups.length / pageSize));
    const safePage = Math.min(page, pageCount - 1);
    const visibleGroups = groups.slice(safePage * pageSize, (safePage + 1) * pageSize);

    useEffect(() => {
        setPage(0);
        setExpandedKeys(new Set());
    }, [resetKey, pageSize]);

    useEffect(() => {
        setExpandedKeys(new Set());
    }, [safePage]);

    useEffect(() => {
        if (!highlightTarget) return;
        const targetIndex = groups.findIndex((group) => rewardHistoryGroupMatchesTarget(group, highlightTarget));
        if (targetIndex < 0) return;
        const targetPage = Math.floor(targetIndex / pageSize);
        if (targetPage !== safePage) {
            setPage(targetPage);
            return;
        }
        const targetKey = groups[targetIndex].key;
        setExpandedKeys((current) => {
            if (current.has(targetKey)) return current;
            const next = new Set(current);
            next.add(targetKey);
            return next;
        });
    }, [highlightTarget?.id, groups, pageSize, safePage]);

    useEffect(() => {
        if (!highlightTarget) return undefined;
        const targetGroup = groups.find((group) => rewardHistoryGroupMatchesTarget(group, highlightTarget));
        if (!targetGroup) return undefined;
        const targetEntry = targetGroup.entries.find((entry) => rewardHistoryEntryMatchesTarget(entry, highlightTarget));
        const timer = window.setTimeout(() => {
            const node = targetEntry ? entryRefs.current[targetEntry.id] : groupRefs.current[targetGroup.key];
            node?.scrollIntoView?.({ behavior: "smooth", block: "center" });
        }, 100);
        return () => window.clearTimeout(timer);
    }, [highlightTarget?.id, groups, safePage, expandedKeys]);

    const toggleGroup = (key) => {
        setExpandedKeys((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    if (!groups.length) return <EmptyState title={emptyTitle}>{emptyText}</EmptyState>;

    return (
        <div className="rw-ticket-history-browser">
            <div className="rw-ticket-history-groups">
                {visibleGroups.map((group) => {
                    const canOpen = Boolean(group.ticketId && onOpenTicket);
                    const latestEntry = group.entries[0];
                    const expanded = expandedKeys.has(group.key);
                    const rewardCountLabel = `${group.entries.length} reward${group.entries.length === 1 ? "" : "s"}`;
                    return (
                        <article ref={(node) => { if (node) groupRefs.current[group.key] = node; else delete groupRefs.current[group.key]; }} className={`rw-ticket-history-group${expanded ? " is-expanded" : ""}${rewardHistoryGroupMatchesTarget(group, highlightTarget) ? " is-notification-highlight" : ""}`} key={group.key}>
                            <button
                                type="button"
                                className="rw-ticket-history-group__toggle"
                                onClick={() => toggleGroup(group.key)}
                                aria-expanded={expanded}
                                aria-controls={`rw-history-${String(group.key).replace(/[^A-Za-z0-9_-]/g, "-")}`}
                            >
                                <span className="rw-ticket-history-group__ticket is-static">
                                    <span className="rw-ticket-history-group__titleline">
                                        <strong>{group.title}</strong>
                                        {!group.ticketId && <span className="rw-ticket-history-group__ref">Account activity</span>}
                                    </span>
                                    <small>{rewardCountLabel} · {formatRewardDate(latestEntry?.created_at || latestEntry?.earned_at)}</small>
                                </span>
                                <span className="rw-ticket-history-group__toggle-meta">
                                    <span>{expanded ? "Hide details" : "View details"}</span>
                                    <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
                                </span>
                            </button>

                            {expanded && (
                                <div className="rw-ticket-history-group__details" id={`rw-history-${String(group.key).replace(/[^A-Za-z0-9_-]/g, "-")}`}>
                                    {canOpen && (
                                        <div className="rw-ticket-history-group__task-action">
                                            <button type="button" onClick={() => onOpenTicket(group.ticketId)}>
                                                Open task <FontAwesomeIcon icon={faArrowRight} />
                                            </button>
                                        </div>
                                    )}
                                    <div className="rw-ticket-history-group__entries">
                                        {group.entries.map((entry) => {
                                            const status = entry.status || "confirmed";
                                            const points = Number(entry.points || 0);
                                            const positive = points >= 0;
                                            const hasBalance = Number.isFinite(Number(entry.balance_before)) && Number.isFinite(Number(entry.balance_after));
                                            const showBalance = hasBalance && (points < 0 || ["redemption", "manual_adjustment"].includes(entry.type));
                                            const exceptionReason = ["rejected", "revoked"].includes(status)
                                                ? (entry.rejection_reason || entry.revoked_reason || entry.reason)
                                                : (entry.type === "manual_adjustment" ? entry.reason : null);
                                            const detailText = entry.description && entry.description !== entry.title ? entry.description : null;
                                            return (
                                                <div ref={(node) => { if (node) entryRefs.current[entry.id] = node; else delete entryRefs.current[entry.id]; }} className={`rw-ticket-history-entry is-${status}${rewardHistoryEntryMatchesTarget(entry, highlightTarget) ? " is-notification-highlight" : ""}`} key={entry.id}>
                                                    <span className="rw-ticket-history-entry__icon"><FontAwesomeIcon icon={TYPE_ICONS[entry.type] || faCoins} /></span>
                                                    <div className="rw-ticket-history-entry__copy">
                                                        <div><strong>{entry.title || REWARD_LABELS[entry.type] || entry.type || "Reward activity"}</strong><StatusPill status={status} /></div>
                                                        <small>{formatRewardDate(entry.created_at || entry.earned_at)}</small>
                                                        {detailText && <span className="rw-ticket-history-entry__detail">{detailText}</span>}
                                                        {showBalance && <span className="rw-ticket-history-entry__balance">Balance {Number(entry.balance_before).toLocaleString("en-GB")} → {Number(entry.balance_after).toLocaleString("en-GB")} pts</span>}
                                                        {exceptionReason && <em>{exceptionReason}</em>}
                                                    </div>
                                                    <div className="rw-ticket-history-entry__points">
                                                        <b className={status === "pending" ? "is-pending-points" : positive ? "is-positive" : "is-negative"}>{formatPoints(points, { sign: true })} pts</b>
                                                        {status !== "confirmed" && <small>{status === "pending" ? "Awaiting review" : status === "rejected" ? "Not awarded" : status}</small>}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </article>
                    );
                })}
            </div>
            <div className="rw-ticket-history-pagination">
                <span>{groups.length === 1 ? "1 task" : `${groups.length} tasks`}</span>
                <AdminPager page={safePage} pageCount={pageCount} onChange={setPage} />
            </div>
        </div>
    );
};

const HistoryView = ({ dashboard, onOpenTicket, highlightTarget = null }) => {
    const historyPager = usePagedRewardHistory(dashboard);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (!highlightTarget?.id) return;
        setFilter("all");
        setSearch("");
    }, [highlightTarget?.id]);
    const filtered = historyPager.entries.filter((entry) => {
        if (filter === "earned" && !(Number(entry.points || 0) > 0 && (entry.status || "confirmed") === "confirmed")) return false;
        if (filter === "redeemed" && entry.type !== "redemption") return false;
        if (filter === "deductions" && !(Number(entry.points || 0) < 0 && entry.type !== "redemption" && (entry.status || "confirmed") === "confirmed")) return false;
        if (filter === "rejected" && !["rejected", "revoked"].includes(entry.status)) return false;
        if (!["all", "earned", "redeemed", "deductions", "rejected"].includes(filter) && entry.status !== filter) return false;
        const term = search.trim().toLowerCase();
        if (!term) return true;
        const ticketId = entry.ticket_id || entry.task_id || "";
        const haystack = `${entry.ticket_title || ""} ${ticketId} ${entry.title || ""} ${REWARD_LABELS[entry.type] || ""}`.toLowerCase();
        return haystack.includes(term.replace(/^#/, "")) || haystack.includes(term);
    });
    const filters = [
        { key: "all", label: "All activity" },
        { key: "earned", label: "Confirmed" },
        { key: "pending", label: "Pending" },
        { key: "deductions", label: "Deductions" },
        { key: "rejected", label: "Rejected / revoked" },
        { key: "redeemed", label: "Spent points" },
    ];
    return (
        <div className="rw-focus-view">
            <FocusHeader title="History" subtitle="See points earned, spent, deducted and reviewed." />
            <div className="rw-history-toolbar">
                <div className="rw-category-filters rw-history-filters">
                    {filters.map((item) => <button key={item.key} type="button" className={filter === item.key ? "is-active" : ""} onClick={() => setFilter(item.key)}>{item.label}</button>)}
                </div>
                <label className="rw-history-search"><FontAwesomeIcon icon={faSearch} /><input className="rw-input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search task" /></label>
            </div>
            <TicketHistoryGroups entries={filtered} onOpenTicket={onOpenTicket} resetKey={`${filter}:${search}`} emptyTitle="No matching activity" emptyText="Try another filter or search." highlightTarget={highlightTarget} />
            {historyPager.hasMore && <div className="rw-history-load-more"><button type="button" className="rw-button is-secondary" disabled={historyPager.loadingMore} onClick={historyPager.loadMore}>{historyPager.loadingMore ? "Loading…" : "Load older activity"}</button></div>}
        </div>
    );
};

const AchievementsView = ({ dashboard, highlightTarget = null }) => {
    const earnedByType = new Map((dashboard.achievements || []).map((achievement) => [achievement.type, achievement]));
    const highlightedType = rewardAchievementType(highlightTarget);
    const highlightRef = useRef(null);

    useEffect(() => {
        if (!highlightedType || !highlightRef.current) return undefined;
        const timer = window.setTimeout(() => highlightRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" }), 80);
        return () => window.clearTimeout(timer);
    }, [highlightTarget?.id, highlightedType]);

    return (
        <div className="rw-focus-view">
            <FocusHeader title="Achievements" subtitle="Recognition for quality, reliability and improvement." />
            <div className="rw-achievement-grid">
                {ACHIEVEMENT_DEFINITIONS.map((definition) => {
                    const earned = earnedByType.get(definition.type);
                    return (
                        <article ref={definition.type === highlightedType ? highlightRef : null} className={`rw-achievement-card${earned ? " is-earned" : ""}${definition.type === highlightedType ? " is-notification-highlight" : ""}`} key={definition.type}>
                            <div className="rw-achievement-card__top"><span><FontAwesomeIcon icon={definition.icon} /></span><StatusPill status={earned ? "approved" : "pending"} /></div>
                            <h3>{definition.title}</h3><p>{definition.description}</p>
                            <div className="rw-achievement-card__foot"><strong>{earned ? achievementRewardLabel(earned) : achievementDefinitionRewardLabel(definition)}</strong><span>{earned ? formatRewardDate(earned.awarded_at) : "Available to earn"}</span></div>
                        </article>
                    );
                })}
            </div>
        </div>
    );
};

const AdminPager = ({ page, pageCount, onChange }) => {
    if (pageCount <= 1) return null;
    return <div className="rw-admin-pager"><button type="button" disabled={page <= 0} onClick={() => onChange(page - 1)}>←</button><span>{page + 1} / {pageCount}</span><button type="button" disabled={page >= pageCount - 1} onClick={() => onChange(page + 1)}>→</button></div>;
};


const TicketPicker = ({ value, onChange, label = "Ticket", optional = false }) => {
    const [queryText, setQueryText] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchError, setSearchError] = useState("");

    useEffect(() => {
        if (value?.id) return undefined;
        const term = queryText.trim();
        if (term.length < 2) {
            setResults([]);
            setSearchError("");
            return undefined;
        }
        let active = true;
        const timer = window.setTimeout(async () => {
            setLoading(true);
            setSearchError("");
            try {
                const items = await searchRewardTickets(term);
                if (active) setResults(items);
            } catch (error) {
                if (active) {
                    setResults([]);
                    setSearchError(error?.message || "Could not search tickets.");
                }
            } finally {
                if (active) setLoading(false);
            }
        }, 250);
        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [queryText, value?.id]);

    const choose = (ticket) => {
        onChange(ticket);
        setQueryText("");
        setResults([]);
        setSearchError("");
    };

    const clear = () => {
        onChange(null);
        setQueryText("");
        setResults([]);
        setSearchError("");
    };

    return (
        <div className="rw-ticket-picker">
            <div className="rw-ticket-picker__label">{label}{optional && <span>optional</span>}</div>
            {value?.id ? (
                <div className="rw-ticket-picker__selected">
                    <div><strong>{value.title || "Untitled ticket"}</strong><span>{value.status || "Unknown status"}</span></div>
                    <button type="button" className="rw-text-button" onClick={clear}>Change</button>
                </div>
            ) : (
                <>
                    <div className="rw-ticket-picker__input">
                        <FontAwesomeIcon icon={faSearch} />
                        <input className="rw-input" value={queryText} onChange={(event) => setQueryText(event.target.value)} placeholder="Search by ticket title…" autoComplete="off" />
                        {loading && <span className="rw-ticket-picker__loading">Searching…</span>}
                    </div>
                    {searchError && <div className="rw-ticket-picker__error">{searchError}</div>}
                    {queryText.trim().length >= 2 && !loading && !searchError && (
                        <div className="rw-ticket-picker__results">
                            {results.length ? results.map((ticket) => (
                                <button type="button" key={ticket.id} onClick={() => choose(ticket)}>
                                    <span><strong>{ticket.title || "Untitled ticket"}</strong><small>{ticket.status || "Unknown status"}</small></span>
                                    <FontAwesomeIcon icon={faArrowRight} />
                                </button>
                            )) : <div className="rw-ticket-picker__empty">No matching tickets found.</div>}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const AdminTeamPanel = ({ teamUsers, onOpenEmployee }) => {
    const [search, setSearch] = useState("");
    const [employeeFilter, setEmployeeFilter] = useState("all");
    const [sort, setSort] = useState("earned_desc");
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 8;
    const employeeOptions = useMemo(() => [...teamUsers]
        .filter((employee) => employee?.id)
        .sort((a, b) => String(a.name || a.email || a.id).localeCompare(String(b.name || b.email || b.id))), [teamUsers]);
    const rows = useMemo(() => [...teamUsers]
        .filter((employee) => {
            if (employeeFilter !== "all" && employee.id !== employeeFilter) return false;
            const term = search.trim().toLowerCase();
            if (!term) return true;
            return `${employee.name || ""} ${employee.email || ""}`.toLowerCase().includes(term);
        })
        .sort((a, b) => {
            const aSummary = a.reward_summary || {};
            const bSummary = b.reward_summary || {};
            if (sort === "balance_desc") return Number(bSummary.confirmed_points || 0) - Number(aSummary.confirmed_points || 0) || String(a.name || a.email || a.id).localeCompare(String(b.name || b.email || b.id));
            if (sort === "pending_desc") return Number(bSummary.pending_points || 0) - Number(aSummary.pending_points || 0) || String(a.name || a.email || a.id).localeCompare(String(b.name || b.email || b.id));
            if (sort === "name_asc") return String(a.name || a.email || a.id).localeCompare(String(b.name || b.email || b.id));
            return Number(bSummary.lifetime_positive_points || 0) - Number(aSummary.lifetime_positive_points || 0) || String(a.name || a.email || a.id).localeCompare(String(b.name || b.email || b.id));
        }), [teamUsers, search, employeeFilter, sort]);
    const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const visibleRows = rows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
    const filtersActive = Boolean(search.trim() || employeeFilter !== "all" || sort !== "earned_desc");

    useEffect(() => { setPage(0); }, [search, employeeFilter, sort]);

    return (
        <section className="rw-admin-table-panel rw-team-points-panel">
            <div className="rw-admin-panel-toolbar">
                <div><strong>Employee points</strong><span>{rows.length} {rows.length === 1 ? "employee" : "employees"}</span></div>
                <div className="rw-admin-filters" aria-label="Team points filters">
                    <label className="rw-history-search"><FontAwesomeIcon icon={faSearch} /><input className="rw-input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search employee" /></label>
                    <label className="rw-filter-field"><select aria-label="Employee" className="rw-input rw-filter-select" value={employeeFilter} onChange={(event) => setEmployeeFilter(event.target.value)}><option value="all">All employees</option>{employeeOptions.map((employee) => <option value={employee.id} key={employee.id}>{employee.name || employee.email || "Unnamed employee"}</option>)}</select></label>
                    <label className="rw-filter-field"><select aria-label="Sort" className="rw-input rw-filter-select" value={sort} onChange={(event) => setSort(event.target.value)}><option value="earned_desc">Lifetime earned</option><option value="balance_desc">Current balance</option><option value="pending_desc">Pending review</option><option value="name_asc">Name A–Z</option></select></label>
                    {filtersActive && <button type="button" className="rw-filter-clear" onClick={() => { setSearch(""); setEmployeeFilter("all"); setSort("earned_desc"); }}>Clear filters</button>}
                </div>
            </div>
            <div className="rw-admin-table-scroll">
                <div className="rw-team-table-head"><div>Employee</div><div>Role</div><div>Lifetime earned</div><div>Current balance</div><div>Pending</div><div /></div>
                {visibleRows.length ? visibleRows.map((employee) => {
                    const summary = employee.reward_summary || {};
                    return (
                        <div className="rw-team-table-row" key={employee.id}>
                            <div><strong>{employee.name || employee.email || employee.id}</strong><span>{employee.email && employee.name ? employee.email : "Rewards member"}</span></div>
                            <div><span>{REWARD_ROLE_LABELS[employee.user_role] || employee.user_role || "User"}</span></div>
                            <div><b className="is-positive">+{Number(summary.lifetime_positive_points || 0).toLocaleString("en-GB")} pts</b></div>
                            <div><b>{Number(summary.confirmed_points || 0).toLocaleString("en-GB")} pts</b></div>
                            <div><span>{Number(summary.pending_points || 0).toLocaleString("en-GB")} pts</span></div>
                            <div><button type="button" className="rw-table-action" onClick={() => onOpenEmployee(employee)}>View history</button></div>
                        </div>
                    );
                }) : <EmptyState title="No rewards members found">No employees match the current filters.</EmptyState>}
            </div>
            <AdminPager page={safePage} pageCount={pageCount} onChange={setPage} />
        </section>
    );
};

const AdminEmployeeHistoryDrawer = ({ employee, onClose, onOpenTicket }) => {
    const { data, loading, error } = useRewardDashboard(employee?.id, Boolean(employee?.id), { includeAdmin: false });
    const dashboard = data || emptyDashboard;
    const summary = dashboard.summary || {};
    const historyPager = usePagedRewardHistory(dashboard);
    const history = historyPager.entries;
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const statusCount = (status) => history.filter((entry) => (entry.status || "confirmed") === status).length;
    const filters = [
        { key: "all", label: "All", count: history.length },
        { key: "confirmed", label: "Confirmed", count: statusCount("confirmed") },
        { key: "pending", label: "Pending", count: statusCount("pending") },
        { key: "rejected", label: "Rejected", count: statusCount("rejected") },
        { key: "revoked", label: "Revoked", count: statusCount("revoked") },
    ].filter((item) => item.key === "all" || item.count > 0);
    const filteredHistory = history.filter((entry) => {
        if (filter !== "all" && (entry.status || "confirmed") !== filter) return false;
        const term = search.trim().toLowerCase();
        if (!term) return true;
        const ticketId = entry.ticket_id || entry.task_id || "";
        const haystack = `${entry.ticket_title || ""} ${ticketId} ${entry.title || ""} ${REWARD_LABELS[entry.type] || ""}`.toLowerCase();
        return haystack.includes(term.replace(/^#/, "")) || haystack.includes(term);
    });

    return (
        <DrawerSurface onClose={onClose} label="Employee reward history" variant="employee-rewards">
            <header className="rw-review-drawer__header rw-employee-rewards-header">
                <div className="rw-heading-block rw-employee-rewards-heading"><span className="rw-eyebrow">Employee rewards</span><h2>{employee?.name || employee?.email || "Employee"}</h2><p>Balance, pending review and reward activity.</p></div>
                <button className="rw-icon-button" type="button" onClick={onClose} aria-label="Close employee rewards"><FontAwesomeIcon icon={faXmark} /></button>
            </header>

            <div className="rw-employee-rewards-body">
                <section className="rw-employee-summary is-compact" aria-label="Employee reward balance">
                    <div className="rw-employee-summary-card is-available"><span>Available</span><strong>{Number(summary.available_points || 0).toLocaleString("en-GB")} <small>pts</small></strong></div>
                    <div className={`rw-employee-summary-card is-pending${Number(summary.pending_points || 0) > 0 ? " has-points" : ""}`}><span>Pending review</span><strong>{Number(summary.pending_points || 0).toLocaleString("en-GB")} <small>pts</small></strong></div>
                    <div className="rw-employee-summary-card is-lifetime"><span>Lifetime earned</span><strong>+{Number(summary.lifetime_positive_points || 0).toLocaleString("en-GB")} <small>pts</small></strong></div>
                </section>

                <section className="rw-employee-history-section" aria-labelledby="rw-employee-history-title">
                    <div className="rw-employee-history-heading">
                        <div><span className="rw-eyebrow">Activity</span><h3 id="rw-employee-history-title">Reward history</h3><p>Search by task or filter by decision.</p></div>
                        <span className="rw-employee-history-count">{filteredHistory.length} loaded</span>
                    </div>
                    <div className="rw-employee-history-toolbar">
                        <label className="rw-history-search is-compact"><FontAwesomeIcon icon={faSearch} /><input className="rw-input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search task" /></label>
                        <div className="rw-team-history-filters" role="group" aria-label="Filter employee reward history">
                            {filters.map((item) => <button type="button" key={item.key} className={filter === item.key ? "is-active" : ""} onClick={() => setFilter(item.key)}>{item.label}<b>{item.count}</b></button>)}
                        </div>
                    </div>
                    <div className="rw-employee-history-content">
                        {loading ? <div className="rw-loading"><span className="rw-spinner" /><strong>Loading history…</strong></div> : error ? <div className="rw-load-error"><strong>History could not be loaded</strong><p>{error}</p></div> : <TicketHistoryGroups entries={filteredHistory} onOpenTicket={onOpenTicket} resetKey={`${filter}:${search}`} emptyTitle={filter === "all" ? "No reward history yet" : `No ${filter} reward entries`} emptyText="No activity matches this view." />}
                        {!loading && !error && historyPager.hasMore && <div className="rw-history-load-more"><button type="button" className="rw-button is-secondary" disabled={historyPager.loadingMore} onClick={historyPager.loadMore}>{historyPager.loadingMore ? "Loading…" : "Load older activity"}</button></div>}
                    </div>
                </section>
            </div>
        </DrawerSurface>
    );
};

const AdminReviewDrawer = ({ review, note, setNote, busy, onClose, onConfirmQuality, onReviewRedemption, onOpenTicket }) => {
    const item = review?.item;
    const isQuality = review?.kind === "quality";
    const [decisions, setDecisions] = useState({});

    useEffect(() => { setDecisions({}); }, [review?.kind, item?.id, item?.work_unit_id]);
    if (!review || !item) return null;

    const rewardDetails = isQuality
        ? ((item.reward_details || []).length
            ? item.reward_details.filter((entry) => Object.prototype.hasOwnProperty.call(QUALITY_REWARD_POINTS, entry.type)).map((entry) => ({ type: entry.type, points: Number(entry.points || QUALITY_REWARD_POINTS[entry.type] || 0) }))
            : (item.reward_types || []).filter((type) => Object.prototype.hasOwnProperty.call(QUALITY_REWARD_POINTS, type)).map((type) => ({ type, points: Number(QUALITY_REWARD_POINTS[type] || 0) })))
        : [];
    const classificationDetails = item.classification_details || {};
    const decidedCount = rewardDetails.filter((entry) => decisions[entry.type]).length;
    const hasReject = rewardDetails.some((entry) => decisions[entry.type] === "reject");
    const readyToSave = !isQuality || (rewardDetails.length > 0 && decidedCount === rewardDetails.length && (!hasReject || note.trim().length >= 4));

    return (
        <DrawerSurface onClose={onClose} label={isQuality ? "Reward review" : "Redemption review"}>
            <header className="rw-review-drawer__header">
                <div className="rw-heading-block"><span className="rw-eyebrow">{isQuality ? "Reward review" : "Redemption request"}</span><h2>{isQuality ? (item.ticket_title || "Untitled task") : item.item_title}</h2><p>{isQuality ? (item.user_name || item.user_id) : "Approve or reject this redemption request."}</p></div>
                <button className="rw-icon-button" type="button" onClick={onClose} aria-label="Close review"><FontAwesomeIcon icon={faXmark} /></button>
            </header>
            <div className="rw-review-drawer__body">
                {!isQuality && <div className="rw-review-facts is-compact"><div><span>Employee</span><strong>{item.user_name || item.user_id}</strong></div><div><span>Cost</span><strong>{Number(item.cost_points || 0).toLocaleString("en-GB")} pts</strong></div><div><span>Requested</span><strong>{formatRewardDate(item.requested_at)}</strong></div></div>}
                {isQuality && <div className="rw-quality-decisions is-simple">
                    {item.ticket_id && onOpenTicket && <button type="button" className="rw-review-open-ticket" onClick={() => onOpenTicket(item.ticket_id)}><span><FontAwesomeIcon icon={faCircleInfo} /> View ticket details</span><FontAwesomeIcon icon={faArrowRight} /></button>}
                    <div className="rw-quality-decision-list">
                        {rewardDetails.map((entry) => {
                            const classification = classificationDetails[entry.type] || {};
                            return <div className={`rw-quality-decision-row${decisions[entry.type] ? ` is-${decisions[entry.type]}` : ""}`} key={entry.type}>
                                <span className="rw-quality-decision-row__icon"><FontAwesomeIcon icon={TYPE_ICONS[entry.type] || faCoins} /></span>
                                <span className="rw-quality-decision-row__copy">
                                    <strong>{REWARD_LABELS[entry.type] || entry.type}</strong>
                                    <small>+{entry.points} pts if approved</small>
                                    <span className="rw-quality-classification-reason"><b>User's reason</b>{classification.reason || "No classification reason was recorded."}</span>
                                </span>
                                <span className="rw-quality-decision-row__actions"><button type="button" className={decisions[entry.type] === "confirm" ? "is-selected is-confirm" : ""} onClick={() => setDecisions((current) => ({ ...current, [entry.type]: "confirm" }))}><FontAwesomeIcon icon={faCheck} /> Approve</button><button type="button" className={decisions[entry.type] === "reject" ? "is-selected is-reject" : ""} onClick={() => setDecisions((current) => ({ ...current, [entry.type]: "reject" }))}><FontAwesomeIcon icon={faXmark} /> Reject</button></span>
                            </div>;
                        })}
                    </div>
                </div>}
                <label className="rw-review-note">{isQuality ? "Review note" : "Review note"}{isQuality && <span>{hasReject ? "required for rejection" : "optional"}</span>}<textarea className="rw-input rw-textarea" value={note} onChange={(event) => setNote(event.target.value)} placeholder={isQuality ? (hasReject ? "Why is the selected reward being rejected?" : "Optional note") : "Reason for this decision"} /></label>
            </div>
            <div className="rw-review-drawer__actions">
                {isQuality ? <><button type="button" className="rw-button is-secondary" disabled={busy} onClick={onClose}>Cancel</button><button type="button" className="rw-button is-primary" disabled={busy || !readyToSave} onClick={() => onConfirmQuality(decisions)}>{busy ? "Saving…" : "Save review"}</button></> : <><button type="button" className="rw-button is-secondary" disabled={busy} onClick={() => onReviewRedemption("reject")}>Reject</button><button type="button" className="rw-button is-primary" disabled={busy} onClick={() => onReviewRedemption("approve")}>{busy ? "Saving…" : "Approve"}</button></>}
            </div>
        </DrawerSurface>
    );
};

const AdminActionDialog = ({ type, setType, teamUsers, busy, onClose, adjustment, setAdjustment, adjustmentTicketPreview, adjustmentTicketLoading, adjustmentTicketError, penalty, setPenalty, penaltyTicketPreview, penaltyTicketLoading, penaltyTicketError, eom, setEom, onSubmit }) => {
    const selectedEmployee = teamUsers.find((employee) => employee.id === adjustment.userId) || null;
    const currentBalance = Number(selectedEmployee?.reward_summary?.confirmed_points || 0);
    const adjustmentPoints = Number(adjustment.points || 0);
    const balanceAfter = currentBalance + (Number.isFinite(adjustmentPoints) ? adjustmentPoints : 0);
    const previewEntries = adjustmentTicketPreview?.entries || [];
    const penaltyPreviewEntries = penaltyTicketPreview?.entries || [];
    return (
        <ModalSurface onClose={onClose} label="New rewards adjustment" wide>
            <div className="rw-dialog__header"><div className="rw-heading-block"><span className="rw-eyebrow">Admin action</span><h2>New reward adjustment</h2><p>Use an explicit audited action rather than editing balances directly.</p></div><button className="rw-icon-button" type="button" onClick={onClose}><FontAwesomeIcon icon={faXmark} /></button></div>
            <div className="rw-action-type-tabs">
                <button type="button" className={type === "adjustment" ? "is-active" : ""} onClick={() => setType("adjustment")}>Add / remove points</button>
                <button type="button" className={type === "penalty" ? "is-active" : ""} onClick={() => setType("penalty")}>Avoidable rework</button>
                <button type="button" className={type === "eom" ? "is-active" : ""} onClick={() => setType("eom")}>Employee of month</button>
            </div>
            <form className="rw-action-form" onSubmit={(event) => onSubmit(event, type)}>
                {type === "adjustment" && <>
                    <label>Employee<select className="rw-input" required value={adjustment.userId} onChange={(event) => setAdjustment((current) => ({ ...current, userId: event.target.value, ticket: null }))}><option value="">Select employee…</option>{teamUsers.map((employee) => <option value={employee.id} key={employee.id}>{teamUserLabel(employee)}</option>)}</select></label>
                    <label>Points<input className="rw-input" type="number" min="-500" max="500" step="1" required value={adjustment.points} onChange={(event) => setAdjustment((current) => ({ ...current, points: event.target.value }))} /></label>
                    {adjustment.userId && <div className="is-wide"><TicketPicker value={adjustment.ticket} onChange={(ticket) => setAdjustment((current) => ({ ...current, ticket }))} label="Related ticket" optional /></div>}
                    <div className="rw-adjustment-balance-preview is-wide">
                        <div><span>Balance before adjustment</span><strong>{currentBalance.toLocaleString("en-GB")} pts</strong></div>
                        <div><span>Balance after adjustment</span><strong className={adjustmentPoints < 0 ? "is-negative" : adjustmentPoints > 0 ? "is-positive" : ""}>{balanceAfter.toLocaleString("en-GB")} pts</strong></div>
                    </div>
                    {adjustment.ticket?.id && <section className="rw-ticket-reward-preview is-wide" aria-label="Selected ticket reward history">
                        <div className="rw-ticket-reward-preview__head"><div><span className="rw-eyebrow">Ticket points before adjustment</span><strong>{adjustment.ticket.title || "Selected ticket"}</strong></div>{adjustmentTicketLoading && <span>Loading…</span>}</div>
                        {adjustmentTicketError ? <div className="rw-ticket-picker__error">{adjustmentTicketError}</div> : !adjustmentTicketLoading && <>
                            <div className="rw-ticket-reward-preview__totals">
                                <span><small>Awarded</small><b>+{Number(adjustmentTicketPreview?.awardedPoints || 0).toLocaleString("en-GB")} pts</b></span>
                                <span><small>Pending</small><b>+{Number(adjustmentTicketPreview?.pendingPoints || 0).toLocaleString("en-GB")} pts</b></span>
                                <span><small>Previous deductions</small><b>{Number(adjustmentTicketPreview?.deductedPoints || 0) ? `−${Number(adjustmentTicketPreview.deductedPoints).toLocaleString("en-GB")}` : "0"} pts</b></span>
                                <span><small>Confirmed net</small><b>{Number(adjustmentTicketPreview?.netConfirmedPoints || 0).toLocaleString("en-GB")} pts</b></span>
                            </div>
                            <div className="rw-ticket-reward-preview__rows">
                                {previewEntries.length ? previewEntries.map((entry) => {
                                    const points = Number(entry.points || 0);
                                    const status = entry.status || "confirmed";
                                    return <div key={entry.id}><span>{REWARD_LABELS[entry.type] || entry.title || entry.type || "Reward"}<small>{status === "confirmed" ? "Confirmed" : status === "pending" ? "Pending" : status === "rejected" ? "Rejected" : status}</small></span><b className={points < 0 ? "is-negative" : points > 0 ? "is-positive" : ""}>{points > 0 ? "+" : ""}{points.toLocaleString("en-GB")} pts</b></div>;
                                }) : <p>No reward entries are recorded for this employee on the selected ticket yet.</p>}
                            </div>
                        </>}
                    </section>}
                    <label className="is-wide">Reason<textarea className="rw-input rw-textarea" required maxLength={1000} value={adjustment.reason} onChange={(event) => setAdjustment((current) => ({ ...current, reason: event.target.value }))} /></label>
                </>}
                {type === "penalty" && <>
                    <div className="is-wide"><TicketPicker value={penalty.ticket} onChange={(ticket) => setPenalty((current) => ({ ...current, ticket }))} label="Ticket" /></div>
                    {penalty.ticket?.id && <section className="rw-ticket-reward-preview is-wide" aria-label="Selected ticket reward history before avoidable rework">
                        <div className="rw-ticket-reward-preview__head"><div><span className="rw-eyebrow">Ticket points before avoidable rework</span><strong>{penalty.ticket.title || "Selected ticket"}</strong></div>{penaltyTicketLoading && <span>Loading…</span>}</div>
                        {penaltyTicketError ? <div className="rw-ticket-picker__error">{penaltyTicketError}</div> : !penaltyTicketLoading && <>
                            <div className="rw-ticket-reward-preview__totals">
                                <span><small>Awarded</small><b>+{Number(penaltyTicketPreview?.awardedPoints || 0).toLocaleString("en-GB")} pts</b></span>
                                <span><small>Pending</small><b>+{Number(penaltyTicketPreview?.pendingPoints || 0).toLocaleString("en-GB")} pts</b></span>
                                <span><small>Previous deductions</small><b>{Number(penaltyTicketPreview?.deductedPoints || 0) ? `−${Number(penaltyTicketPreview.deductedPoints).toLocaleString("en-GB")}` : "0"} pts</b></span>
                                <span><small>Confirmed net</small><b>{Number(penaltyTicketPreview?.netConfirmedPoints || 0).toLocaleString("en-GB")} pts</b></span>
                            </div>
                            <div className="rw-ticket-reward-preview__rows">
                                {penaltyPreviewEntries.length ? penaltyPreviewEntries.map((entry) => {
                                    const points = Number(entry.points || 0);
                                    const status = entry.status || "confirmed";
                                    return <div key={entry.id}><span>{REWARD_LABELS[entry.type] || entry.title || entry.type || "Reward"}<small>{status === "confirmed" ? "Confirmed" : status === "pending" ? "Pending" : status === "rejected" ? "Rejected" : status}</small></span><b className={points < 0 ? "is-negative" : points > 0 ? "is-positive" : ""}>{points > 0 ? "+" : ""}{points.toLocaleString("en-GB")} pts</b></div>;
                                }) : <p>No reward entries are recorded for the employee on this ticket yet.</p>}
                            </div>
                        </>}
                    </section>}
                    <label>Impact<select className="rw-input" value={penalty.impact} onChange={(event) => setPenalty((current) => ({ ...current, impact: event.target.value }))}><option value="significant_repeat_work">Significant repeat work</option><option value="customer_impact">Customer impact</option><option value="missed_critical_information">Missed critical information</option><option value="other">Other</option></select></label>
                    <label className="is-wide">Reason<textarea className="rw-input rw-textarea" required minLength={20} maxLength={1000} value={penalty.reason} onChange={(event) => setPenalty((current) => ({ ...current, reason: event.target.value }))} /></label>
                </>}
                {type === "eom" && <>
                    <label>Employee<select className="rw-input" required value={eom.userId} onChange={(event) => setEom((current) => ({ ...current, userId: event.target.value }))}><option value="">Select employee…</option>{teamUsers.map((employee) => <option value={employee.id} key={employee.id}>{teamUserLabel(employee)}</option>)}</select></label>
                    <label>Month<input className="rw-input" type="month" required value={eom.monthKey} onChange={(event) => setEom((current) => ({ ...current, monthKey: event.target.value }))} /></label>
                    <label>Cash<select className="rw-input" value={eom.cashValue} onChange={(event) => setEom((current) => ({ ...current, cashValue: event.target.value }))}><option value="50">£50</option><option value="75">£75</option><option value="100">£100</option></select></label>
                    <label className="is-wide">Reason<textarea className="rw-input rw-textarea" required maxLength={1000} value={eom.reason} onChange={(event) => setEom((current) => ({ ...current, reason: event.target.value }))} /></label>
                </>}
                <div className="rw-dialog__actions rw-action-form__actions"><button type="button" className="rw-button is-secondary" onClick={onClose}>Cancel</button><button type="submit" className={type === "penalty" ? "rw-button is-danger" : "rw-button is-primary"} disabled={Boolean(busy)}>{busy ? "Saving…" : type === "penalty" ? "Apply penalty" : type === "eom" ? "Record award" : "Create adjustment"}</button></div>
            </form>
        </ModalSurface>
    );
};

const IncidentDialog = ({ incident, setIncident, teamUsers, busy, onClose, onSubmit }) => {
    return (
        <ModalSurface onClose={onClose} label="Record reward incident">
            <div className="rw-dialog__header"><div className="rw-heading-block"><span className="rw-eyebrow">Management</span><h2>Record reward incident</h2><p>Use this for complaints, attendance or quality evidence that affects reward eligibility.</p></div><button className="rw-icon-button" type="button" onClick={onClose}><FontAwesomeIcon icon={faXmark} /></button></div>
            <form className="rw-incident-form" onSubmit={onSubmit}>
                <label>Employee<select className="rw-input" required value={incident.userId} onChange={(event) => setIncident((current) => ({ ...current, userId: event.target.value }))}><option value="">Select employee…</option>{teamUsers.map((employee) => <option value={employee.id} key={employee.id}>{teamUserLabel(employee)}</option>)}</select></label>
                <label>Incident type<select className="rw-input" value={incident.type} onChange={(event) => setIncident((current) => ({ ...current, type: event.target.value }))}><option value="quality_correction">Quality correction</option><option value="complaint">Complaint</option><option value="attendance">Attendance</option><option value="other">Other</option></select></label>
                <TicketPicker value={incident.ticket} onChange={(ticket) => setIncident((current) => ({ ...current, ticket }))} label="Related ticket" optional />
                <label>Description<textarea className="rw-input rw-textarea" required maxLength={1000} value={incident.description} onChange={(event) => setIncident((current) => ({ ...current, description: event.target.value }))} /></label>
                <div className="rw-dialog__actions"><button type="button" className="rw-button is-secondary" onClick={onClose}>Cancel</button><button type="submit" className="rw-button is-primary" disabled={busy}>{busy ? "Saving…" : "Record incident"}</button></div>
            </form>
        </ModalSurface>
    );
};

const AdminTab = ({ dashboard, refresh, notify, onOpenTicket }) => {
    const viewedRole = dashboard.user?.user_role === "coordinator2" ? "coordinator" : dashboard.user?.user_role;
    const isAdmin = dashboard.viewer?.role === "admin" && viewedRole === "admin";
    const [adminView, setAdminView] = useState("team");
    const [page, setPage] = useState({ quality: 0, redemptions: 0, audit: 0 });
    const [qualityFilter, setQualityFilter] = useState({ search: "", type: "all", age: "all", sort: "oldest" });
    const [redemptionFilter, setRedemptionFilter] = useState({ search: "", category: "all", age: "all", sort: "oldest" });
    const [auditFilter, setAuditFilter] = useState({ search: "", type: "all", status: "all", employee: "all", age: "30", sort: "newest" });
    const [review, setReview] = useState(null);
    const [reviewNote, setReviewNote] = useState("");
    const [busy, setBusy] = useState("");
    const [actionOpen, setActionOpen] = useState(false);
    const [actionType, setActionType] = useState("adjustment");
    const [incidentOpen, setIncidentOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeDrawerSuspended, setEmployeeDrawerSuspended] = useState(false);
    const [reviewDrawerSuspended, setReviewDrawerSuspended] = useState(false);
    const [adjustment, setAdjustment] = useState({ userId: "", points: "", reason: "", ticket: null });
    const [adjustmentTicketPreview, setAdjustmentTicketPreview] = useState(null);
    const [adjustmentTicketLoading, setAdjustmentTicketLoading] = useState(false);
    const [adjustmentTicketError, setAdjustmentTicketError] = useState("");
    const [penalty, setPenalty] = useState({ ticket: null, impact: "significant_repeat_work", reason: "" });
    const [penaltyTicketPreview, setPenaltyTicketPreview] = useState(null);
    const [penaltyTicketLoading, setPenaltyTicketLoading] = useState(false);
    const [penaltyTicketError, setPenaltyTicketError] = useState("");
    const [eom, setEom] = useState({ userId: "", monthKey: new Date().toISOString().slice(0, 7), cashValue: "50", reason: "" });
    const [incident, setIncident] = useState({ userId: "", type: "quality_correction", ticket: null, description: "" });
    const teamUsers = dashboard.team_users || [];
    const pendingQuality = dashboard.pending_quality || [];
    const pendingRedemptions = dashboard.pending_redemptions || [];
    const recentIncidents = dashboard.recent_incidents || [];
    const recentAwards = dashboard.recent_awards || [];
    const recentAudit = dashboard.recent_audit || [];
    const PAGE_SIZE = 6;

    const valueTime = (value) => {
        if (!value) return 0;
        if (typeof value?.toMillis === "function") return value.toMillis();
        if (typeof value?.seconds === "number") return value.seconds * 1000;
        const parsed = new Date(value).getTime();
        return Number.isFinite(parsed) ? parsed : 0;
    };
    const passesAge = (value, age) => {
        if (age === "all") return true;
        const time = valueTime(value);
        if (!time) return false;
        const now = new Date();
        if (age === "today") {
            const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
            return time >= start;
        }
        const days = Number(age || 0);
        return days > 0 ? time >= Date.now() - (days * 86400000) : true;
    };
    const paginate = (items, key) => {
        const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
        const safe = Math.min(page[key] || 0, pageCount - 1);
        return { page: safe, pageCount, items: items.slice(safe * PAGE_SIZE, (safe + 1) * PAGE_SIZE) };
    };

    const qualityRows = useMemo(() => pendingQuality.filter((item) => {
        if (qualityFilter.type !== "all" && !(item.reward_types || []).includes(qualityFilter.type)) return false;
        if (!passesAge(item.created_at, qualityFilter.age)) return false;
        const term = qualityFilter.search.trim().toLowerCase();
        if (!term) return true;
        const rewards = (item.reward_types || []).map((type) => REWARD_LABELS[type] || type).join(" ");
        return `${item.user_name || item.user_id || ""} ${item.ticket_title || ""} ${item.ticket_id || ""} ${rewards}`.toLowerCase().includes(term.replace(/^#/, ""));
    }).sort((a, b) => qualityFilter.sort === "newest" ? valueTime(b.created_at) - valueTime(a.created_at) : valueTime(a.created_at) - valueTime(b.created_at)), [pendingQuality, qualityFilter]);
    const qualityData = paginate(qualityRows, "quality");

    const catalogById = useMemo(() => new Map((dashboard.catalog || []).map((item) => [item.id, item])), [dashboard.catalog]);
    const redemptionCategory = (item) => item.item_category || catalogById.get(item.item_id)?.category || "other";
    const redemptionCategories = useMemo(() => [...new Set(pendingRedemptions.map(redemptionCategory).filter(Boolean))].sort(), [pendingRedemptions, catalogById]);
    const redemptionRows = useMemo(() => pendingRedemptions.filter((item) => {
        if (redemptionFilter.category !== "all" && redemptionCategory(item) !== redemptionFilter.category) return false;
        if (!passesAge(item.requested_at, redemptionFilter.age)) return false;
        const term = redemptionFilter.search.trim().toLowerCase();
        if (!term) return true;
        return `${item.user_name || item.user_id || ""} ${item.item_title || ""}`.toLowerCase().includes(term);
    }).sort((a, b) => redemptionFilter.sort === "newest" ? valueTime(b.requested_at) - valueTime(a.requested_at) : valueTime(a.requested_at) - valueTime(b.requested_at)), [pendingRedemptions, redemptionFilter, catalogById]);
    const redemptionData = paginate(redemptionRows, "redemptions");

    const auditRows = useMemo(() => (recentAudit.length ? recentAudit.map((item) => ({
        id: item.id,
        kind: item.type === "quality_review" ? "Reward review" : item.type === "redemption_review" ? "Redemption" : item.type === "manual_adjustment" ? "Adjustment" : item.type === "avoidable_rework" ? "Avoidable rework" : item.type === "employee_of_month" ? "Employee of month" : item.type === "classification" ? "Classification" : item.type === "incident" ? "Incident" : item.type || "Audit",
        who: item.user_name,
        reviewer: item.reviewer_name,
        status: item.status || "recorded",
        detail: item.detail || (item.decisions || []).map((decision) => `${REWARD_LABELS[decision.type] || decision.type}: ${decision.decision}${decision.points ? ` ${decision.points} pts` : ""}`).join(" · ") || item.reason || "—",
        date: item.occurred_at || item.created_at,
    })) : [
        ...recentIncidents.map((item) => ({ id: `i-${item.id}`, kind: "Incident", who: item.user_name, status: "recorded", detail: item.description, date: item.occurred_at })),
        ...recentAwards.map((item) => ({ id: `a-${item.id}`, kind: item.type === "employee_of_month" ? "Employee of month" : "Award", who: item.user_name, status: "awarded", detail: item.reason, date: item.approved_at })),
    ]), [recentAudit, recentIncidents, recentAwards]);
    const auditTypes = useMemo(() => [...new Set(auditRows.map((row) => row.kind).filter(Boolean))].sort(), [auditRows]);
    const auditStatuses = useMemo(() => [...new Set(auditRows.map((row) => row.status).filter(Boolean))].sort(), [auditRows]);
    const auditEmployees = useMemo(() => [...new Set(auditRows.map((row) => row.who).filter(Boolean))].sort(), [auditRows]);
    const filteredAuditRows = useMemo(() => auditRows.filter((row) => {
        if (auditFilter.type !== "all" && row.kind !== auditFilter.type) return false;
        if (auditFilter.status !== "all" && row.status !== auditFilter.status) return false;
        if (auditFilter.employee !== "all" && row.who !== auditFilter.employee) return false;
        if (!passesAge(row.date, auditFilter.age)) return false;
        const term = auditFilter.search.trim().toLowerCase();
        if (!term) return true;
        return `${row.kind || ""} ${row.who || ""} ${row.reviewer || ""} ${row.detail || ""}`.toLowerCase().includes(term);
    }).sort((a, b) => auditFilter.sort === "oldest" ? valueTime(a.date) - valueTime(b.date) : valueTime(b.date) - valueTime(a.date)), [auditRows, auditFilter]);
    const auditData = paginate(filteredAuditRows, "audit");

    useEffect(() => { setPage((current) => ({ ...current, quality: 0 })); }, [qualityFilter]);
    useEffect(() => { setPage((current) => ({ ...current, redemptions: 0 })); }, [redemptionFilter]);
    useEffect(() => { setPage((current) => ({ ...current, audit: 0 })); }, [auditFilter]);

    const openReview = async (kind, item) => {
        let currentItem = item;
        if (kind === "quality" && item?.user_id && item?.work_unit_id) {
            try {
                const liveContext = await loadPendingQualityReviewContext(item.ticket_id, item.user_id, item.work_unit_id);
                const liveRewards = liveContext.rewards || [];
                currentItem = {
                    ...item,
                    reward_types: liveRewards.map((entry) => entry.type),
                    reward_details: liveRewards,
                    pending_points: liveRewards.reduce((sum, entry) => sum + Number(entry.points || 0), 0),
                    classification_details: liveContext.classification_details || {},
                };
            } catch (_) {
            }
        }
        setReview({ kind, item: currentItem });
        setReviewNote("");
    };

    useEffect(() => {
        if (!review || busy === "review") return;
        const source = review.kind === "quality" ? pendingQuality : pendingRedemptions;
        const liveItem = review.kind === "quality"
            ? source.find((entry) => (review.item?.id && entry.id === review.item.id) || (entry.work_unit_id && entry.work_unit_id === review.item?.work_unit_id))
            : source.find((entry) => entry.id === review.item?.id);
        if (!liveItem) {
            setReview(null);
            setReviewNote("");
            notify("success", "This review item was already handled. The queue is up to date.");
            return;
        }
        setReview((current) => current ? {
            ...current,
            item: {
                ...current.item,
                ...liveItem,
                classification_details: current.item?.classification_details || liveItem.classification_details || {},
            },
        } : current);
    }, [pendingQuality, pendingRedemptions, review?.kind, review?.item?.id, review?.item?.work_unit_id, busy]);

    useEffect(() => {
        if (!employeeDrawerSuspended) return undefined;
        const restoreEmployeeDrawer = () => setEmployeeDrawerSuspended(false);
        window.addEventListener("tickets:drawer-closed", restoreEmployeeDrawer);
        return () => window.removeEventListener("tickets:drawer-closed", restoreEmployeeDrawer);
    }, [employeeDrawerSuspended]);

    useEffect(() => {
        if (!reviewDrawerSuspended) return undefined;
        const restoreReviewDrawer = () => setReviewDrawerSuspended(false);
        window.addEventListener("tickets:drawer-closed", restoreReviewDrawer);
        return () => window.removeEventListener("tickets:drawer-closed", restoreReviewDrawer);
    }, [reviewDrawerSuspended]);


    useEffect(() => {
        if (!actionOpen || actionType !== "adjustment" || !adjustment.userId || !adjustment.ticket?.id) {
            setAdjustmentTicketPreview(null);
            setAdjustmentTicketLoading(false);
            setAdjustmentTicketError("");
            return undefined;
        }
        let active = true;
        setAdjustmentTicketLoading(true);
        setAdjustmentTicketError("");
        loadTicketRewardBreakdown(adjustment.userId, adjustment.ticket.id)
            .then((preview) => { if (active) setAdjustmentTicketPreview(preview); })
            .catch((error) => {
                if (active) {
                    setAdjustmentTicketPreview(null);
                    setAdjustmentTicketError(error?.message || "Could not load this ticket's reward history.");
                }
            })
            .finally(() => { if (active) setAdjustmentTicketLoading(false); });
        return () => { active = false; };
    }, [actionOpen, actionType, adjustment.userId, adjustment.ticket?.id]);

    useEffect(() => {
        if (!actionOpen || actionType !== "penalty" || !penalty.ticket?.id) {
            setPenaltyTicketPreview(null);
            setPenaltyTicketLoading(false);
            setPenaltyTicketError("");
            return undefined;
        }
        let active = true;
        setPenaltyTicketLoading(true);
        setPenaltyTicketError("");
        resolveRewardWorkUnit(penalty.ticket.id, { requireCompleted: true })
            .then(async (workUnit) => {
                if (!workUnit?.id || !workUnit?.user_id) throw new Error("No completed reward work was found for the selected ticket.");
                const preview = await loadTicketRewardBreakdown(workUnit.user_id, penalty.ticket.id);
                if (active) setPenaltyTicketPreview(preview);
            })
            .catch((error) => {
                if (active) {
                    setPenaltyTicketPreview(null);
                    setPenaltyTicketError(error?.message || "Could not load this ticket's reward history.");
                }
            })
            .finally(() => { if (active) setPenaltyTicketLoading(false); });
        return () => { active = false; };
    }, [actionOpen, actionType, penalty.ticket?.id]);

    if (!isAdmin) return null;

    const openEmployeeRewardTicket = async (ticketId) => {
        if (!ticketId || !onOpenTicket) return false;
        setEmployeeDrawerSuspended(true);
        try {
            const opened = await onOpenTicket(ticketId);
            if (!opened) setEmployeeDrawerSuspended(false);
            return opened;
        } catch (error) {
            setEmployeeDrawerSuspended(false);
            throw error;
        }
    };

    const openReviewRewardTicket = async (ticketId) => {
        if (!ticketId || !onOpenTicket) return false;
        setReviewDrawerSuspended(true);
        try {
            const opened = await onOpenTicket(ticketId);
            if (!opened) setReviewDrawerSuspended(false);
            return opened;
        } catch (error) {
            setReviewDrawerSuspended(false);
            throw error;
        }
    };

    const confirmQuality = async (decisions) => {
        const item = review?.item;
        if (!item) return;
        const qualityDecisions = Object.fromEntries(Object.entries(decisions || {}).filter(([type]) => Object.prototype.hasOwnProperty.call(QUALITY_REWARD_POINTS, type)));
        const rejected = Object.values(qualityDecisions).some((decision) => decision === "reject");
        if (rejected && reviewNote.trim().length < 4) return notify("error", "Add a reason for the rejected reward(s).");
        setBusy("review");
        try {
            const result = await reviewTicketQuality(item.ticket_id, item.work_unit_id, qualityDecisions, reviewNote.trim());
            const confirmed = Number(result?.confirmedPoints || 0);
            const rejectedPoints = Number(result?.rejectedPoints || 0);
            notify("success", `Review saved${confirmed ? ` · +${confirmed} approved` : ""}${rejectedPoints ? ` · ${rejectedPoints} rejected` : ""}.`);
            setReview(null); setReviewNote(""); await refresh();
        } catch (error) { notify("error", error.message); } finally { setBusy(""); }
    };

    const reviewRedemptionRequest = async (decision) => {
        const item = review?.item;
        if (!item || !reviewNote.trim()) return notify("error", "Add a review note first.");
        setBusy("review");
        try {
            await reviewRedemption(item.id, decision, reviewNote.trim());
            notify("success", `Redemption ${decision === "approve" ? "approved" : "rejected"}.`);
            setReview(null); setReviewNote(""); await refresh();
        } catch (error) { notify("error", error.message); } finally { setBusy(""); }
    };

    const submitAdminAction = async (event, type) => {
        event.preventDefault(); setBusy(type);
        try {
            if (type === "adjustment") {
                await createManualAdjustment({
                    userId: adjustment.userId,
                    points: Number(adjustment.points),
                    reason: adjustment.reason.trim(),
                    ticketId: adjustment.ticket?.id || null,
                });
                setAdjustment({ userId: "", points: "", reason: "", ticket: null });
                setAdjustmentTicketPreview(null);
                notify("success", "Manual reward adjustment created.");
            } else if (type === "penalty") {
                if (!penalty.ticket?.id) throw new Error("Select a ticket from the search results first.");
                const workUnit = await resolveRewardWorkUnit(penalty.ticket.id, { requireCompleted: true });
                if (!workUnit?.id) throw new Error("No completed reward work was found for the selected ticket.");
                await applyAvoidableReworkPenalty({ ticketId: penalty.ticket.id, workUnitId: workUnit.id, impact: penalty.impact, reason: penalty.reason.trim() });
                setPenalty({ ticket: null, impact: "significant_repeat_work", reason: "" });
                setPenaltyTicketPreview(null);
                setPenaltyTicketError("");
                notify("success", "Avoidable rework penalty applied.");
            } else {
                await awardEmployeeOfMonth({ ...eom, cashValue: Number(eom.cashValue) });
                setEom((current) => ({ ...current, userId: "", reason: "" }));
                notify("success", "Employee of the Month award recorded.");
            }
            setActionOpen(false); await refresh();
        } catch (error) { notify("error", error.message); } finally { setBusy(""); }
    };

    const submitIncident = async (event) => {
        event.preventDefault(); setBusy("incident");
        try {
            const workUnit = incident.ticket?.id ? await resolveRewardWorkUnit(incident.ticket.id, { userId: incident.userId }) : null;
            await recordRewardIncident({ userId: incident.userId, type: incident.type, ticketId: incident.ticket?.id || null, workUnitId: workUnit?.id || null, description: incident.description.trim() });
            setIncident((current) => ({ ...current, ticket: null, description: "" }));
            setIncidentOpen(false); notify("success", "Reward incident recorded."); await refresh();
        } catch (error) { notify("error", error.message); } finally { setBusy(""); }
    };

    const tabs = [
        { key: "team", label: "Team points", count: teamUsers.length },
        { key: "quality", label: "Reward review", count: pendingQuality.length },
        { key: "redemptions", label: "Redemptions", count: pendingRedemptions.length },
        { key: "management", label: "Management" },
        { key: "audit", label: "Audit" },
    ];
    const qualityFiltersActive = Boolean(qualityFilter.search || qualityFilter.type !== "all" || qualityFilter.age !== "all" || qualityFilter.sort !== "oldest");
    const redemptionFiltersActive = Boolean(redemptionFilter.search || redemptionFilter.category !== "all" || redemptionFilter.age !== "all" || redemptionFilter.sort !== "oldest");
    const auditFiltersActive = Boolean(auditFilter.search || auditFilter.type !== "all" || auditFilter.status !== "all" || auditFilter.employee !== "all" || auditFilter.age !== "30" || auditFilter.sort !== "newest");

    return (
        <div className="rw-focus-view rw-admin-workbench">
            <FocusHeader title="Rewards administration" subtitle="Review decisions, points and audited exceptions." actions={<>{adminView === "management" && <button type="button" className="rw-button is-secondary" onClick={() => setIncidentOpen(true)}>Record incident</button>}{isAdmin && <button type="button" className="rw-button is-primary" onClick={() => setActionOpen(true)}>New adjustment</button>}</>} />
            <nav className="rw-admin-tabs" aria-label="Rewards administration sections">
                {tabs.map((tab) => <button type="button" key={tab.key} className={adminView === tab.key ? "is-active" : ""} onClick={() => setAdminView(tab.key)}>{tab.label}{typeof tab.count === "number" && <b>{tab.count}</b>}</button>)}
            </nav>

            {adminView === "team" && <AdminTeamPanel teamUsers={teamUsers} onOpenEmployee={setSelectedEmployee} />}

            {adminView === "quality" && <section className="rw-admin-table-panel">
                <div className="rw-admin-panel-toolbar">
                    <div><strong>Pending reward review</strong><span>{qualityRows.length} item{qualityRows.length === 1 ? "" : "s"}</span></div>
                    <div className="rw-admin-filters" aria-label="Reward review filters">
                        <label className="rw-history-search"><FontAwesomeIcon icon={faSearch} /><input className="rw-input" value={qualityFilter.search} onChange={(event) => setQualityFilter((current) => ({ ...current, search: event.target.value }))} placeholder="Search employee or task" /></label>
                        <label className="rw-filter-field"><select aria-label="Reward" className="rw-input rw-filter-select" value={qualityFilter.type} onChange={(event) => setQualityFilter((current) => ({ ...current, type: event.target.value }))}><option value="all">All review rewards</option><option value="difficult_task">Difficult task</option><option value="urgent_management_request">Urgent request</option></select></label>
                        <label className="rw-filter-field"><select aria-label="Age" className="rw-input rw-filter-select" value={qualityFilter.age} onChange={(event) => setQualityFilter((current) => ({ ...current, age: event.target.value }))}><option value="all">All dates</option><option value="today">Today</option><option value="7">Last 7 days</option><option value="30">Last 30 days</option></select></label>
                        <label className="rw-filter-field"><select aria-label="Sort" className="rw-input rw-filter-select" value={qualityFilter.sort} onChange={(event) => setQualityFilter((current) => ({ ...current, sort: event.target.value }))}><option value="oldest">Oldest first</option><option value="newest">Newest first</option></select></label>
                        {qualityFiltersActive && <button type="button" className="rw-filter-clear" onClick={() => setQualityFilter({ search: "", type: "all", age: "all", sort: "oldest" })}>Clear filters</button>}
                    </div>
                </div>
                <div className="rw-admin-table-scroll">
                    <div className="rw-admin-table-head"><div>Employee</div><div>Task</div><div>Potential</div><div>Created</div><div /></div>
                    {qualityData.items.length ? qualityData.items.map((item) => <div className="rw-admin-table-row" key={item.work_unit_id || item.ticket_id}><div><strong>{item.user_name || item.user_id}</strong></div><div><strong>{item.ticket_title || "Task"}</strong><span>{(item.reward_types || []).map((type) => REWARD_LABELS[type] || type).join(" · ")}</span></div><div><b className="is-positive">+{(item.reward_details || []).reduce((sum, entry) => sum + Number(entry.points || QUALITY_REWARD_POINTS[entry.type] || 0), 0)} pts</b></div><div><span>{formatRewardDate(item.created_at)}</span></div><div><button type="button" className="rw-table-action" onClick={() => openReview("quality", item)}>Review</button></div></div>) : <EmptyState icon={faCheck} title="No rewards need review">Difficult and urgent rewards waiting for a decision will appear here.</EmptyState>}
                </div>
                <AdminPager page={qualityData.page} pageCount={qualityData.pageCount} onChange={(value) => setPage((current) => ({ ...current, quality: value }))} />
            </section>}

            {adminView === "redemptions" && <section className="rw-admin-table-panel">
                <div className="rw-admin-panel-toolbar">
                    <div><strong>Pending redemptions</strong><span>{redemptionRows.length} request{redemptionRows.length === 1 ? "" : "s"}</span></div>
                    <div className="rw-admin-filters" aria-label="Redemption filters">
                        <label className="rw-history-search"><FontAwesomeIcon icon={faSearch} /><input className="rw-input" value={redemptionFilter.search} onChange={(event) => setRedemptionFilter((current) => ({ ...current, search: event.target.value }))} placeholder="Search employee or reward" /></label>
                        <label className="rw-filter-field"><select aria-label="Category" className="rw-input rw-filter-select" value={redemptionFilter.category} onChange={(event) => setRedemptionFilter((current) => ({ ...current, category: event.target.value }))}><option value="all">All categories</option>{redemptionCategories.map((category) => <option value={category} key={category}>{CATEGORY_LABELS[category] || category}</option>)}</select></label>
                        <label className="rw-filter-field"><select aria-label="Age" className="rw-input rw-filter-select" value={redemptionFilter.age} onChange={(event) => setRedemptionFilter((current) => ({ ...current, age: event.target.value }))}><option value="all">All dates</option><option value="today">Today</option><option value="7">Last 7 days</option><option value="30">Last 30 days</option></select></label>
                        <label className="rw-filter-field"><select aria-label="Sort" className="rw-input rw-filter-select" value={redemptionFilter.sort} onChange={(event) => setRedemptionFilter((current) => ({ ...current, sort: event.target.value }))}><option value="oldest">Oldest first</option><option value="newest">Newest first</option></select></label>
                        {redemptionFiltersActive && <button type="button" className="rw-filter-clear" onClick={() => setRedemptionFilter({ search: "", category: "all", age: "all", sort: "oldest" })}>Clear filters</button>}
                    </div>
                </div>
                <div className="rw-admin-table-scroll">
                    <div className="rw-admin-table-head"><div>Employee</div><div>Reward</div><div>Points</div><div>Requested</div><div /></div>
                    {redemptionData.items.length ? redemptionData.items.map((item) => <div className="rw-admin-table-row" key={item.id}><div><strong>{item.user_name || item.user_id}</strong></div><div><strong>{item.item_title}</strong><span>{CATEGORY_LABELS[redemptionCategory(item)] || redemptionCategory(item)}</span></div><div><b>{Number(item.cost_points || 0).toLocaleString("en-GB")} pts</b></div><div><span>{formatRewardDate(item.requested_at)}</span></div><div><button type="button" className="rw-table-action" onClick={() => openReview("redemption", item)}>Review</button></div></div>) : <EmptyState icon={faCheck} title="No redemption requests">No requests match the current filters.</EmptyState>}
                </div>
                <AdminPager page={redemptionData.page} pageCount={redemptionData.pageCount} onChange={(value) => setPage((current) => ({ ...current, redemptions: value }))} />
            </section>}

            {adminView === "management" && <div className="rw-management-grid">
                <section className="rw-management-card"><span className="rw-management-icon"><FontAwesomeIcon icon={faCircleInfo} /></span><div><h3>Reward incidents</h3><p>Record quality corrections, complaints, attendance or other evidence that affects achievement eligibility.</p><button type="button" className="rw-text-button" onClick={() => setIncidentOpen(true)}>Record an incident <FontAwesomeIcon icon={faArrowRight} /></button></div></section>
                <section className="rw-management-card"><span className="rw-management-icon"><FontAwesomeIcon icon={faBolt} /></span><div><h3>Ticket classification</h3><p>Difficult and urgent flags stay attached to the completed work and require a management decision before points are spendable.</p><div className="rw-management-facts"><span><b>Speed</b> automatic within 10 minutes</span><span><b>Difficult</b> management review</span><span><b>Urgent</b> management review</span></div></div></section>
            </div>}

            {adminView === "audit" && <section className="rw-admin-table-panel">
                <div className="rw-admin-panel-toolbar">
                    <div><strong>Audit activity</strong><span>{filteredAuditRows.length} event{filteredAuditRows.length === 1 ? "" : "s"}</span></div>
                    <div className="rw-admin-filters is-wrap" aria-label="Audit filters">
                        <label className="rw-history-search"><FontAwesomeIcon icon={faSearch} /><input className="rw-input" value={auditFilter.search} onChange={(event) => setAuditFilter((current) => ({ ...current, search: event.target.value }))} placeholder="Search audit" /></label>
                        <label className="rw-filter-field"><select aria-label="Type" className="rw-input rw-filter-select" value={auditFilter.type} onChange={(event) => setAuditFilter((current) => ({ ...current, type: event.target.value }))}><option value="all">All types</option>{auditTypes.map((type) => <option value={type} key={type}>{type}</option>)}</select></label>
                        <label className="rw-filter-field"><select aria-label="Status" className="rw-input rw-filter-select" value={auditFilter.status} onChange={(event) => setAuditFilter((current) => ({ ...current, status: event.target.value }))}><option value="all">All statuses</option>{auditStatuses.map((status) => <option value={status} key={status}>{String(status).replaceAll("_", " ")}</option>)}</select></label>
                        <label className="rw-filter-field"><select aria-label="Employee" className="rw-input rw-filter-select" value={auditFilter.employee} onChange={(event) => setAuditFilter((current) => ({ ...current, employee: event.target.value }))}><option value="all">All employees</option>{auditEmployees.map((name) => <option value={name} key={name}>{name}</option>)}</select></label>
                        <label className="rw-filter-field"><select aria-label="Date" className="rw-input rw-filter-select" value={auditFilter.age} onChange={(event) => setAuditFilter((current) => ({ ...current, age: event.target.value }))}><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="all">All dates</option></select></label>
                        <label className="rw-filter-field"><select aria-label="Sort" className="rw-input rw-filter-select" value={auditFilter.sort} onChange={(event) => setAuditFilter((current) => ({ ...current, sort: event.target.value }))}><option value="newest">Newest first</option><option value="oldest">Oldest first</option></select></label>
                        {auditFiltersActive && <button type="button" className="rw-filter-clear" onClick={() => setAuditFilter({ search: "", type: "all", status: "all", employee: "all", age: "30", sort: "newest" })}>Clear filters</button>}
                    </div>
                </div>
                <div className="rw-admin-table-scroll">
                    <div className="rw-admin-table-head rw-admin-table-head--audit"><div>Type</div><div>Employee</div><div>Detail</div><div>Date</div></div>
                    {auditData.items.length ? auditData.items.map((row) => <div className="rw-admin-table-row rw-admin-table-row--audit" key={row.id}><div><StatusPill status={row.status === "partially_confirmed" ? "partially_confirmed" : row.status === "applied" || row.status === "recorded" || row.status === "awarded" ? "confirmed" : row.status} /><span>{row.kind}</span></div><div><strong>{row.who || "—"}</strong>{row.reviewer && <span>by {row.reviewer}</span>}</div><div><span>{row.detail || "—"}</span></div><div><span>{formatRewardDate(row.date)}</span></div></div>) : <EmptyState title="No audit activity">No events match the current filters.</EmptyState>}
                </div>
                <AdminPager page={auditData.page} pageCount={auditData.pageCount} onChange={(value) => setPage((current) => ({ ...current, audit: value }))} />
            </section>}

            {selectedEmployee && !employeeDrawerSuspended && <AdminEmployeeHistoryDrawer employee={selectedEmployee} onClose={() => setSelectedEmployee(null)} onOpenTicket={openEmployeeRewardTicket} />}
            {review && !reviewDrawerSuspended && <AdminReviewDrawer review={review} note={reviewNote} setNote={setReviewNote} busy={busy === "review"} onClose={() => !busy && setReview(null)} onConfirmQuality={confirmQuality} onReviewRedemption={reviewRedemptionRequest} onOpenTicket={openReviewRewardTicket} />}
            {actionOpen && isAdmin && <AdminActionDialog type={actionType} setType={setActionType} teamUsers={teamUsers} busy={busy} onClose={() => !busy && setActionOpen(false)} adjustment={adjustment} setAdjustment={setAdjustment} adjustmentTicketPreview={adjustmentTicketPreview} adjustmentTicketLoading={adjustmentTicketLoading} adjustmentTicketError={adjustmentTicketError} penalty={penalty} setPenalty={setPenalty} penaltyTicketPreview={penaltyTicketPreview} penaltyTicketLoading={penaltyTicketLoading} penaltyTicketError={penaltyTicketError} eom={eom} setEom={setEom} onSubmit={submitAdminAction} />}
            {incidentOpen && <IncidentDialog incident={incident} setIncident={setIncident} teamUsers={teamUsers} busy={busy === "incident"} onClose={() => !busy && setIncidentOpen(false)} onSubmit={submitIncident} />}
        </div>
    );
};

const RewardsPage = ({ userId, viewedUserRole = "", isOpen = true, onClose = null, embedded = false, onOpenTicket = null, navigationTarget = null, onNavigationHandled = null }) => {
    const normalizedViewedRole = viewedUserRole === "coordinator2" ? "coordinator" : viewedUserRole;
    const viewedRoleIsAdmin = normalizedViewedRole === "admin";
    const { data, loading, error, refresh } = useRewardDashboard(userId, isOpen || embedded, { includeAdmin: viewedRoleIsAdmin });
    const dashboard = data || emptyDashboard;
    const dashboardViewedRole = dashboard.user?.user_role === "coordinator2" ? "coordinator" : dashboard.user?.user_role;
    const effectiveViewedRole = normalizedViewedRole || dashboardViewedRole || "";
    const isAdmin = dashboard.viewer?.role === "admin" && effectiveViewedRole === "admin";
    const isViewingAs = Boolean(dashboard.viewer?.id && dashboard.user?.id && dashboard.viewer.id !== dashboard.user.id);
    const viewedUserName = dashboard.user?.name || dashboard.user?.email || dashboard.user?.id || "employee";
    const viewedUserRoleLabel = REWARD_ROLE_LABELS[effectiveViewedRole] || effectiveViewedRole || "User";
    const [view, setView] = useState("home");
    const [notice, setNotice] = useState(null);
    const [notificationHighlight, setNotificationHighlight] = useState(null);

    useEffect(() => {
        if (embedded || !isOpen || typeof document === "undefined") return undefined;
        document.body.classList.add("rw-rewards-shell-open");
        const onKeyDown = (event) => {
            if (event.key !== "Escape") return;
            const nestedOverlayOpen = document.querySelector("[data-rw-layer=\"true\"]");
            if (!nestedOverlayOpen) onClose?.();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.body.classList.remove("rw-rewards-shell-open");
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [embedded, isOpen, onClose]);

    useEffect(() => {
        if (view === "admin" && !isAdmin) setView("home");
    }, [view, isAdmin]);

    useEffect(() => {
        setView("home");
        setNotice(null);
        setNotificationHighlight(null);
    }, [userId]);

    useEffect(() => {
        if (!navigationTarget?.id) return;
        setNotificationHighlight(navigationTarget);
        setView(rewardNotificationView(navigationTarget));
        onNavigationHandled?.();
    }, [navigationTarget?.id]);

    useEffect(() => {
        if (!notificationHighlight?.id || loading) return undefined;
        const timer = window.setTimeout(() => setNotificationHighlight(null), 4000);
        return () => window.clearTimeout(timer);
    }, [notificationHighlight?.id, loading]);

    const notify = (type, message) => {
        setNotice({ type, message });
        window.setTimeout(() => setNotice(null), 5000);
    };

    if (!isOpen && !embedded) return null;

    const content = (
        <div className={`rw-page${embedded ? " is-embedded" : " is-modal"}${view === "admin" ? " is-admin-view" : ""}`}>
            {!embedded && <header className="rw-page__header"><div className="rw-page__brand"><span><FontAwesomeIcon icon={faTrophy} /></span><div><small>Tickets rewards</small><h1>Rewards & recognition</h1></div></div><div className="rw-page__header-actions"><button type="button" className="rw-icon-button" onClick={refresh} title="Refresh rewards"><FontAwesomeIcon icon={faRotate} /></button>{onClose && <button type="button" className="rw-icon-button" onClick={onClose} aria-label="Close rewards"><FontAwesomeIcon icon={faXmark} /></button>}</div></header>}
            <main className="rw-page__body">
                {notice && <div className={`rw-toast is-${notice.type}`}>{notice.message}</div>}
                {loading && <div className="rw-loading"><span className="rw-spinner" /><strong>Loading rewards…</strong></div>}
                {!loading && error && <div className="rw-load-error"><FontAwesomeIcon icon={faCircleInfo} /><strong>Rewards could not be loaded</strong><p>{error}</p><button type="button" className="rw-button is-primary" onClick={refresh}>Try again</button></div>}
                {!loading && !error && (
                    <>
                        <nav className="rw-subtabs" aria-label="Rewards sections">
                            <button type="button" className={view === "home" ? "is-active" : ""} onClick={() => setView("home")}>Overview</button>
                            <button type="button" className={view === "catalog" ? "is-active" : ""} onClick={() => setView("catalog")}>Rewards</button>
                            <button type="button" className={view === "history" ? "is-active" : ""} onClick={() => setView("history")}>History</button>
                            <button type="button" className={view === "achievements" ? "is-active" : ""} onClick={() => setView("achievements")}>Achievements</button>
                            {isAdmin && <button type="button" className={view === "admin" ? "is-active" : ""} onClick={() => setView("admin")}>Admin</button>}
                        </nav>
                        <div className="rw-subtab-panel">
                            {view === "home" && <RewardsHome dashboard={dashboard} refresh={refresh} notify={notify} setView={setView} readOnly={isViewingAs} />}
                            {view === "catalog" && <CatalogView dashboard={dashboard} refresh={refresh} notify={notify} readOnly={isViewingAs} highlightTarget={notificationHighlight} />}
                            {view === "history" && <HistoryView dashboard={dashboard} onOpenTicket={onOpenTicket} highlightTarget={notificationHighlight} />}
                            {view === "achievements" && <AchievementsView dashboard={dashboard} highlightTarget={notificationHighlight} />}
                            {view === "admin" && isAdmin && <AdminTab dashboard={dashboard} refresh={refresh} notify={notify} onOpenTicket={onOpenTicket} />}
                        </div>
                    </>
                )}
            </main>
        </div>
    );

    if (embedded) return content;
    if (!isOpen || typeof document === "undefined") return null;

    return createPortal(
        <div
            className="rw-rewards-shell"
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose?.();
            }}
        >
            <section
                className="rw-rewards-window"
                role="dialog"
                aria-modal="true"
                aria-label="Rewards & recognition"
                onMouseDown={(event) => event.stopPropagation()}
            >
                {content}
            </section>
        </div>,
        document.body
    );
};

export default RewardsPage;
